/**
 * GuestListTab.jsx
 * 
 * Full translation from v1.1-admin-uplift GuestListPanel.tsx (3,044 lines)
 * Source: remotes/origin/v1.1-admin-uplift:frontend/app/admin/GuestListPanel.tsx
 * 
 * Checklist Categories:
 * - [x] Panel Header (title + subtitle + Add Guest button)
 * - [x] Metrics Strip (8 KPIs)
 * - [x] Guest Ops Cards (Journey Control + CSV Import)
 * - [x] Bulk Bar
 * - [x] Add Guest Form
 * - [x] Toolbar (Search + Select Menus + Utility Buttons)
 * - [x] Table Structure (7 columns)
 * - [x] Table Row Elements
 * - [x] Guest Journey Cell (with tooltip)
 * - [x] Guest Alert Cell (with tooltip)
 * - [x] CSV Import Modal
 * - [x] Journey Config Modal
 * - [x] Avatar Menu Modal
 * - [x] Empty State
 * - [x] Web Tooltip Portal
 * - [x] External Search Indicator
 * - [x] Avatar Spinner
 * - [x] GuestDetailSheet integration
 */

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { formatPhoneInput } from '../../lib/vendorInsights';
import axios from 'axios';
import {
  Search,
  UserPlus,
  X,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Upload,
  Download,
  GitCompare,
  Check,
  Camera,
  Image as ImageIcon,
  Trash2,
  Users,
  AlertCircle,
  Zap,
  User,
  Loader2,
  ExternalLink,
  Key,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
import { toast } from 'sonner';
import { isVideoGuestbookEnabled } from './video-guestbook/videoGuestbookFlag';
import KPIIntegrityIndicator from './KPIIntegrityIndicator';
import AdminPageHeader from './AdminPageHeader';
import './adminStyles.css';
import GuestDetailSheet from './GuestDetailSheet';
import { theme } from '../../data/colors';

// ─── Design Tokens (mapped to theme) ────────────────────────────────────────
const COLORS = {
  gold: theme.accentPrimary,
  goldDark: theme.sidebarSectionLabel,
  goldLight: '#F8F2E5',
  cream: theme.bgCard,
  creamDark: '#FCF8F0',
  text: theme.textPrimary,
  textMuted: theme.textMuted,
  textLight: theme.textPlaceholder,
  border: theme.borderMedium,
  borderLight: theme.borderLight,
  success: theme.statusSuccess,
  successBg: theme.statusSuccessTint,
  successBorder: 'rgba(95,158,122,0.34)',
  warning: theme.statusWarning,
  warningBg: theme.statusWarningTint,
  warningBorder: 'rgba(209,170,83,0.34)',
  danger: theme.statusError,
  dangerBg: theme.statusErrorTint,
  dangerBorder: 'rgba(214,119,119,0.34)',
  grey: '#B5AEA1',
};

// ─── Utility Functions ─────────────────────────────────────────────────────────
function formatAddedDate(value) {
  if (!value) return 'Not available';
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getGuestInitials(guest) {
  const first = guest.first_name?.trim().charAt(0) || '';
  const last = guest.last_name?.trim().charAt(0) || '';
  return `${first}${last}`.toUpperCase() || '?';
}

function formatGroupingLabel(guest) {
  // Solo guests (singleton party or orphan) must show NOTHING in the Grouping column.
  // A guest is "grouped" only when their party has 2+ real (non-deleted) members.
  // This matches the same isGrouped rule used in GuestDetailSheet.
  const memberCount = Number(guest?.party_member_count || 0);
  if (memberCount < 2) return '';
  // Source-of-truth for the Grouping column is admin-typed only:
  //   1. guest.group_name  (admin-typed via Profile)
  //   2. guest.household   (admin-imported via CSV)
  //   3. otherwise blank
  // We deliberately do NOT fall back to guest.party_name here — that field can
  // contain auto-stamped "<Surname> Party" strings which are not admin-typed
  // and were leaking into the Grouping column. (Feb 2026 drift correction.)
  return guest.group_name?.trim() || guest.household?.trim() || '';
}

// Normalize RSVP status to lowercase for consistent comparison
function rsvpIs(guest, ...statuses) {
  const r = (guest.rsvp_status || '').toLowerCase();
  return statuses.some(s => r === s);
}

// Guest Issues Detection (simplified version of guestIssues.ts)
function getGuestIssues(guest) {
  const issues = [];
  
  if (!guest.email) {
    issues.push({
      key: 'missing-email',
      title: 'Missing Email',
      description: 'No email address on file',
      severity: 'warning',
      guidance: 'Add an email to enable digital invitations',
    });
  }
  
  if (!guest.phone) {
    issues.push({
      key: 'missing-phone',
      title: 'Missing Phone',
      description: 'No phone number on file',
      severity: 'info',
      guidance: 'Add a phone number for SMS reminders',
    });
  }
  
  if (guest.invitation_status === 'failed') {
    issues.push({
      key: 'delivery-failed',
      title: 'Delivery Failed',
      description: 'Last invitation attempt failed',
      severity: 'critical',
      guidance: 'Check email/phone and retry sending',
    });
  }
  
  if (rsvpIs(guest, 'no', 'declined')) {
    issues.push({
      key: 'declined',
      title: 'RSVP Declined',
      description: 'Guest has declined the invitation',
      severity: 'warning',
      guidance: 'Consider following up or updating records',
    });
  }
  
  return issues;
}

// CSV Parsing
function parseCsvText(raw) {
  const lines = raw.trim().split('\n').filter(Boolean);
  if (lines.length < 2) return [];
  
  // Normalize headers: trim whitespace, lowercase, handle common variations
  const rawHeader = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
  const header = rawHeader.map(h => h.toLowerCase().trim());
  
  // Flexible column matching with common variations
  const findIndex = (variations) => {
    for (const v of variations) {
      const idx = header.findIndex(h => h === v || h.includes(v));
      if (idx >= 0) return idx;
    }
    return -1;
  };
  
  // Support common header variations
  const idxFirst = findIndex(['first_name', 'first', 'firstname', 'name']);
  const idxLast = findIndex(['last_name', 'last', 'lastname', 'surname']);
  const idxEmail = findIndex(['email', 'e-mail', 'email_address']);
  const idxPhone = findIndex(['phone', 'mobile', 'cell', 'phone_number', 'telephone']);
  const idxParty = findIndex(['party', 'party_name', 'household', 'group', 'family']);
  
  const parsed = [];
  const warnings = [];
  
  lines.slice(1).forEach((line, lineNum) => {
    const cols = line.split(',').map(c => c.trim().replace(/^"|"$/g, ''));
    const firstName = idxFirst >= 0 ? (cols[idxFirst] || '').trim() : '';
    const lastName = idxLast >= 0 ? (cols[idxLast] || '').trim() : '';
    const email = idxEmail >= 0 ? (cols[idxEmail] || '').trim().toLowerCase() : '';
    const phone = idxPhone >= 0 ? (cols[idxPhone] || '').trim() : '';
    const party = idxParty >= 0 ? (cols[idxParty] || '').trim() : '';
    
    // Only require first_name to be present
    if (!firstName) {
      warnings.push(`Row ${lineNum + 2}: Missing first name, skipped`);
      return;
    }
    
    const row = {
      first_name: firstName,
      last_name: lastName,
      email: email,
      phone: phone,
      party_name: party,
      // Track if contact info is missing for UI feedback
      _has_contact: !!(email || phone),
    };
    
    parsed.push(row);
  });
  
  return { rows: parsed, warnings };
}

// CSV Export
function buildExportRows(guests) {
  const headers = [
    'First Name',
    'Last Name',
    'Email',
    'Phone',
    'Age Group',
    'RSVP Status',
    'Invitation Status',
    'Source',
    'Source Detail',
    'Group Name',
    'Is Primary',
    'Invitation Token',
    'Invitation URL',
    'Confirmation Token',
    'Confirmation URL',
    'Added',
  ];
  const origin = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  const rows = guests.map(g => {
    const inviteToken = g.party_rsvp_token || '';
    const confToken = g.party_celebration_token || '';
    return [
      g.first_name || '',
      g.last_name || '',
      g.email || '',
      g.phone || '',
      g.age_group || '',
      g.rsvp_status || '',
      g.invitation_status || '',
      g.source || '',
      g.source_detail || '',
      g.party_name || g.group_name || g.household || '',
      g.is_primary ? 'Yes' : '',
      inviteToken,
      inviteToken ? `${origin}/experience/${inviteToken}` : '',
      confToken,
      confToken ? `${origin}/celebration/${confToken}` : '',
      g.created_at || '',
    ];
  });
  return { headers, rows };
}

function buildCsv(guests) {
  const { headers, rows } = buildExportRows(guests);
  const escape = (v) => {
    const s = v == null ? '' : String(v);
    return (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r'))
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  const out = [headers.map(escape).join(',')];
  rows.forEach(r => out.push(r.map(escape).join(',')));
  return out.join('\r\n');
}

function exportCsv(guests) {
  const csv = buildCsv(guests);
  // UTF-8 BOM so Excel opens it with correct encoding as well.
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'guests_export.csv';
  a.click();
  URL.revokeObjectURL(url);
}

async function exportXlsx(guests) {
  // Dynamic import to keep bundle lean.
  const ExcelJS = (await import('exceljs')).default || (await import('exceljs'));
  const { headers, rows } = buildExportRows(guests);
  const wb = new ExcelJS.Workbook();
  wb.creator = 'M-Vitations';
  wb.created = new Date();
  const ws = wb.addWorksheet('Guests');
  ws.columns = headers.map((h, i) => ({
    header: h,
    key: `col_${i}`,
    width: [14, 14, 26, 16, 10, 12, 16, 12, 24, 22, 10, 14, 44, 22][i] || 16,
  }));
  // Header styling
  const headerRow = ws.getRow(1);
  headerRow.font = { bold: true };
  headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0E8D4' } };
  headerRow.alignment = { vertical: 'middle', horizontal: 'left' };
  // Freeze top row
  ws.views = [{ state: 'frozen', ySplit: 1 }];
  // Append rows — force URL Token (col 12, idx 11) as text so Excel doesn't mangle it
  rows.forEach(r => {
    const rowValues = {};
    headers.forEach((_, i) => { rowValues[`col_${i}`] = r[i] ?? ''; });
    const addedRow = ws.addRow(rowValues);
    const tokenCell = addedRow.getCell('col_11');
    tokenCell.numFmt = '@';
    tokenCell.value = r[11] || '';
    const urlCell = addedRow.getCell('col_12');
    if (r[12]) {
      urlCell.value = { text: r[12], hyperlink: r[12] };
      urlCell.font = { color: { argb: 'FF1D4ED8' }, underline: true };
    }
  });
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'guests_export.xlsx';
  a.click();
  URL.revokeObjectURL(url);
}

function exportGuests(guests, format = 'csv') {
  if (format === 'xlsx') return exportXlsx(guests);
  return exportCsv(guests);
}

// ─── Styles Object ─────────────────────────────────────────────────────────────
const styles = {
  // Panel Header
  panelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 18,
    gap: 16,
    flexWrap: 'wrap',
  },
  panelTitleBlock: {
    flex: 1,
    minWidth: 200,
  },
  panelTitle: {
    fontSize: 26,
    fontWeight: 700,
    color: COLORS.text,
    letterSpacing: -0.5,
    margin: 0,
  },
  panelSubtitle: {
    marginTop: 4,
    fontSize: 13,
    color: COLORS.textMuted,
    lineHeight: 1.5,
  },
  addGuestBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minHeight: 42,
    borderRadius: 12,
    padding: '8px 12px',
    background: `linear-gradient(180deg, ${COLORS.gold} 0%, #B89A4A 100%)`,
    border: 'none',
    color: '#fff',
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.72,
    cursor: 'pointer',
    boxShadow: '0px 12px 22px rgba(198,167,90,0.24)',
  },
  addGuestBtnIcon: {
    width: 22,
    height: 22,
    borderRadius: 999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255,255,255,0.18)',
    border: '1px solid rgba(255,255,255,0.18)',
  },
  
  // Metrics Strip
  metricsStrip: {
    display: 'flex',
    flexWrap: 'wrap',
    borderRadius: 14,
    border: `1px solid ${COLORS.border}`,
    borderTop: `2px solid ${COLORS.gold}`,
    background: COLORS.cream,
    marginBottom: 18,
    boxShadow: '0px 20px 36px rgba(18,18,18,0.06), inset 0px 1px 0px rgba(255,255,255,0.94)',
    position: 'relative',
  },
  metricCell: {
    flex: '1 1 100px',
    minHeight: 88,
    padding: '16px 18px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    borderRight: `1px solid ${COLORS.borderLight}`,
  },
  metricCellLast: {
    borderRight: 'none',
  },
  metricLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: COLORS.textLight,
    textTransform: 'uppercase',
    letterSpacing: 0.9,
    marginBottom: 6,
  },
  metricValue: {
    fontSize: 24,
    fontWeight: 700,
    color: '#181713',
    letterSpacing: -0.6,
  },
  
  // Guest Ops Row
  opsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 14,
    marginBottom: 18,
  },
  opsCard: {
    flex: '1 1 280px',
    borderRadius: 14,
    border: `1px solid ${COLORS.border}`,
    borderTop: `2px solid ${COLORS.gold}`,
    background: COLORS.cream,
    padding: 18,
    boxShadow: '0px 24px 42px rgba(18,18,18,0.07), 0px 8px 18px rgba(18,18,18,0.04), inset 0px 1px 0px rgba(255,255,255,0.94)',
    // Fixed-height review window: header stays put, the list region scrolls
    // internally so the box never grows as more guests are added.
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 480,
    overflow: 'hidden',
  },
  opsCardPrimary: {
    background: theme.bgCard,
  },
  opsEyebrow: {
    fontSize: 10,
    fontWeight: 700,
    color: COLORS.textLight,
    textTransform: 'uppercase',
    letterSpacing: 0.9,
  },
  opsTitle: {
    marginTop: 8,
    fontSize: 17,
    fontWeight: 700,
    color: '#1F1C17',
    letterSpacing: -0.25,
  },
  opsBody: {
    marginTop: 8,
    fontSize: 12,
    lineHeight: 1.5,
    color: '#6F6658',
  },
  opsTokenRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 12,
  },
  opsToken: {
    borderRadius: 999,
    border: `1px solid ${theme.borderLight}`,
    background: theme.bgTableDivider,
    padding: '6px 10px',
    fontSize: 10,
    fontWeight: 700,
    color: COLORS.goldDark,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  opsHint: {
    marginTop: 12,
    fontSize: 11,
    color: COLORS.textMuted,
  },
  opsChecklist: {
    marginTop: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  opsChecklistItem: {
    fontSize: 11,
    color: '#6F6658',
  },
  opsPrimaryBtn: {
    marginTop: 14,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    borderRadius: 12,
    background: COLORS.gold,
    padding: '11px 14px',
    border: 'none',
    color: '#fff',
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    cursor: 'pointer',
    boxShadow: '0px 12px 22px rgba(198,167,90,0.24)',
  },
  opsSecondaryBtn: {
    marginTop: 14,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    borderRadius: 12,
    border: '1px solid rgba(198,167,90,0.28)',
    background: theme.bgCard,
    padding: '11px 14px',
    color: COLORS.goldDark,
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    cursor: 'pointer',
    boxShadow: '0px 10px 18px rgba(18,18,18,0.05), inset 0px 1px 0px rgba(255,255,255,0.92)',
  },
  
  // Bulk Bar
  bulkBar: {
    marginBottom: 18,
    borderRadius: 14,
    border: `1px solid ${COLORS.border}`,
    borderTop: `2px solid ${COLORS.gold}`,
    background: COLORS.cream,
    padding: '14px 18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    boxShadow: '0px 20px 34px rgba(18,18,18,0.06), 0px 6px 14px rgba(18,18,18,0.04), inset 0px 1px 0px rgba(255,255,255,0.94)',
  },
  bulkBarText: {
    fontSize: 12,
    fontWeight: 700,
    color: '#463F34',
  },
  bulkBarActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  bulkAction: {
    borderRadius: 999,
    border: `1px solid ${COLORS.border}`,
    background: theme.bgCard,
    padding: '8px 12px',
    fontSize: 11,
    fontWeight: 700,
    color: COLORS.goldDark,
    textTransform: 'uppercase',
    letterSpacing: 0.7,
    cursor: 'pointer',
  },
  bulkActionGhost: {
    padding: '8px 10px',
    background: 'none',
    border: 'none',
    fontSize: 11,
    fontWeight: 700,
    color: '#7D7466',
    textTransform: 'uppercase',
    letterSpacing: 0.7,
    cursor: 'pointer',
  },
  
  // Add Guest Card
  addGuestCard: {
    marginBottom: 18,
    borderRadius: 14,
    border: `1px solid ${COLORS.border}`,
    borderTop: `2px solid ${COLORS.gold}`,
    background: COLORS.cream,
    padding: 18,
    boxShadow: '0px 20px 36px rgba(18,18,18,0.06), inset 0px 1px 0px rgba(255,255,255,0.94)',
  },
  cardEyebrow: {
    fontSize: 10,
    fontWeight: 700,
    color: COLORS.textLight,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 12,
  },
  formRow: {
    display: 'flex',
    gap: 10,
    marginBottom: 10,
    flexWrap: 'wrap',
  },
  formInput: {
    flex: '1 1 140px',
    borderRadius: 12,
    border: '1px solid rgba(198,167,90,0.18)',
    background: theme.bgCard,
    padding: '11px 12px',
    fontSize: 13,
    color: COLORS.text,
    outline: 'none',
  },
  addGuestSubmit: {
    marginTop: 4,
    borderRadius: 12,
    background: COLORS.gold,
    padding: '11px 16px',
    border: 'none',
    color: '#fff',
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    cursor: 'pointer',
  },
  
  // Toolbar Card
  toolbarCard: {
    marginBottom: 18,
    borderRadius: 14,
    border: `1px solid ${COLORS.border}`,
    borderTop: `2px solid ${COLORS.gold}`,
    background: COLORS.cream,
    padding: 16,
    boxShadow: '0px 20px 36px rgba(18,18,18,0.06), inset 0px 1px 0px rgba(255,255,255,0.94)',
  },
  toolbarRow: {
    display: 'flex',
    alignItems: 'stretch',
    flexWrap: 'wrap',
    gap: 12,
  },
  searchField: {
    flex: '1 1 320px',
    maxWidth: 420,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    borderRadius: 12,
    border: '1.5px solid rgba(198,167,90,0.55)',
    background: theme.bgCard,
    padding: '0 12px',
    minHeight: 44,
  },
  searchInput: {
    flex: 1,
    border: 'none',
    background: 'none',
    fontSize: 12,
    color: COLORS.text,
    outline: 'none',
  },
  selectWrap: {
    display: 'flex',
    gap: 8,
    flexWrap: 'nowrap',
  },
  selectPill: {
    minWidth: 126,
    borderRadius: 12,
    border: '1.5px solid rgba(198,167,90,0.55)',
    background: theme.bgCard,
    padding: '11px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    cursor: 'pointer',
    position: 'relative',
    boxShadow: '0px 10px 18px rgba(18,18,18,0.04), inset 0px 1px 0px rgba(255,255,255,0.92)',
  },
  selectLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: COLORS.textLight,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  selectValue: {
    flex: 1,
    fontSize: 12,
    fontWeight: 600,
    color: '#463F34',
  },
  selectDropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    marginTop: 4,
    width: 220,
    borderRadius: 14,
    border: `1px solid ${COLORS.border}`,
    background: theme.bgCard,
    overflow: 'hidden',
    zIndex: 100,
    boxShadow: '0px 20px 40px rgba(18,18,18,0.15)',
  },
  selectMenuItem: {
    padding: '12px 14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    borderTop: '1px solid rgba(198,167,90,0.16)',
  },
  selectMenuItemFirst: {
    borderTop: 'none',
  },
  selectMenuText: {
    fontSize: 12,
    fontWeight: 600,
    color: '#463F34',
  },
  selectMenuTextActive: {
    color: COLORS.goldDark,
  },
  controlActionsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginLeft: 'auto',
    flexWrap: 'wrap',
  },
  utilityBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    borderRadius: 12,
    border: '1.5px solid rgba(198,167,90,0.55)',
    background: theme.bgCard,
    padding: '9px 12px',
    minHeight: 38,
    fontSize: 10,
    fontWeight: 700,
    color: COLORS.goldDark,
    textTransform: 'uppercase',
    letterSpacing: 0.75,
    cursor: 'pointer',
    boxShadow: '0px 2px 8px rgba(24,20,14,0.10)',
  },
  
  // External Search Indicator
  searchFieldExternal: {
    borderColor: COLORS.gold,
    background: theme.statusWarningTint,
    boxShadow: '0 0 0 2px rgba(198,167,90,0.15)',
  },
  externalSearchBadge: {
    borderRadius: 999,
    padding: '3px 8px',
    background: COLORS.gold,
    fontSize: 9,
    fontWeight: 700,
    color: '#fff',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  
  // Avatar Spinner
  avatarSpinner: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255,255,255,0.66)',
    borderRadius: '50%',
  },
  
  // Tooltip Portal
  tooltipPortal: {
    position: 'fixed',
    zIndex: 9999,
    pointerEvents: 'none',
  },
  tooltipCard: {
    minWidth: 220,
    maxWidth: 320,
    borderRadius: 14,
    border: '1px solid rgba(198,167,90,0.35)',
    background: '#FDFBF7',
    padding: 14,
    boxShadow: '0 8px 32px rgba(18,18,18,0.18), 0 2px 8px rgba(18,18,18,0.10), inset 0 1px 0 rgba(255,255,255,0.8)',
    pointerEvents: 'auto',
  },
  tooltipEyebrow: {
    fontSize: 9,
    fontWeight: 700,
    color: COLORS.textLight,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 8,
  },
  tooltipTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: COLORS.text,
    marginBottom: 4,
  },
  tooltipMeta: {
    fontSize: 11,
    color: COLORS.textMuted,
    lineHeight: 1.45,
    marginBottom: 6,
  },
  tooltipRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 0',
    borderTop: '1px solid rgba(198,167,90,0.12)',
  },
  tooltipRowLabel: {
    fontSize: 10,
    fontWeight: 600,
    color: COLORS.textMuted,
  },
  tooltipRowValue: {
    fontSize: 11,
    fontWeight: 600,
    color: '#463F34',
  },
  tooltipIssueRow: {
    padding: '8px 0',
    borderTop: '1px solid rgba(198,167,90,0.12)',
  },
  tooltipIssueTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: '#463F34',
  },
  tooltipIssueDesc: {
    fontSize: 10,
    color: COLORS.textMuted,
    marginTop: 2,
    lineHeight: 1.4,
  },
  tooltipIssueGuidance: {
    fontSize: 10,
    color: COLORS.gold,
    marginTop: 4,
    fontStyle: 'italic',
  },
  
  // Table
  tableCard: {
    borderRadius: 14,
    border: `1px solid ${COLORS.border}`,
    borderTop: `2px solid ${COLORS.gold}`,
    background: COLORS.cream,
    overflow: 'hidden',
    boxShadow: '0px 28px 48px rgba(18,18,18,0.08), 0px 10px 22px rgba(18,18,18,0.05), inset 0px 1px 0px rgba(255,255,255,0.94)',
  },
  tableFrame: {
    minWidth: 1320,
    overflowX: 'auto',
  },
  tableHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: '14px 18px',
    background: theme.bgCard,
    borderBottom: '1px solid rgba(198,167,90,0.18)',
    boxSizing: 'border-box',
  },
  headerCell: {
    fontSize: 10,
    fontWeight: 700,
    color: COLORS.textLight,
    textTransform: 'uppercase',
    letterSpacing: 0.9,
  },
  tableBody: {
    // Allow natural page scrolling - no internal scroll constraint
  },
  tableRow: {
    display: 'flex',
    alignItems: 'stretch',
    width: '100%',
    padding: '16px 18px',
    borderBottom: '1px solid rgba(226,220,207,0.7)',
    background: theme.bgCard,
    transition: 'background 150ms ease',
    boxSizing: 'border-box',
  },
  tableRowSelected: {
    background: theme.bgCard,
  },
  tableRowHover: {
    background: theme.bgCard,
  },
  checkboxCell: {
    width: 40,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  checkbox: {
    width: 17,
    height: 17,
    borderRadius: 5,
    border: '1.35px solid rgba(198,167,90,0.52)',
    background: theme.bgCard,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0px 6px 12px rgba(18,18,18,0.04), inset 0px 1px 0px rgba(255,255,255,0.92)',
  },
  checkboxChecked: {
    background: COLORS.gold,
    border: `1.35px solid ${COLORS.gold}`,
    boxShadow: '0px 8px 14px rgba(198,167,90,0.18), inset 0px 1px 0px rgba(255,255,255,0.26)',
  },
  bodyCell: {
    paddingRight: 12,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  nameCol: { width: 280, flexShrink: 0 },
  groupCol: { width: 160, flexShrink: 0 },
  rsvpCol:  { width: 150, flexShrink: 0 },
  partyCol: { width: 120, flexShrink: 0 },
  journeyCol: { width: 330, flexShrink: 0 },
  alertCol: { width: 170, flexShrink: 0, display: 'flex', justifyContent: 'center' },
  actionCol: { width: 84, flexShrink: 0, alignItems: 'flex-end' },
  
  identityRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  avatarButton: {
    width: 42,
    height: 42,
    borderRadius: 21,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid rgba(198,167,90,0.28)',
    background: theme.bgCard,
    overflow: 'hidden',
    cursor: 'pointer',
    flexShrink: 0,
    position: 'relative',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  avatarSpinner: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255,255,255,0.66)',
  },
  initialsText: {
    color: COLORS.goldDark,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.8,
  },
  guestName: {
    fontSize: 15,
    fontWeight: 700,
    color: COLORS.text,
    letterSpacing: -0.2,
    cursor: 'pointer',
    marginBottom: 4,
  },
  guestContact: {
    fontSize: 12,
    color: COLORS.textSecondary,
    lineHeight: 1.5,
  },
  guestMeta: {
    marginTop: 2,
    fontSize: 11,
    color: COLORS.textMuted,
    lineHeight: 1.35,
  },
  groupingName: {
    fontSize: 12.5,
    fontWeight: 700,
    color: '#463F34',
  },
  groupingMeta: {
    marginTop: 3,
    fontSize: 11,
    color: COLORS.textMuted,
    textTransform: 'capitalize',
  },
  rsvpBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 10px',
    borderRadius: 8,
    border: '1px solid',
  },
  rsvpSuccess: {
    background: 'rgba(22,163,74,0.10)',
    borderColor: 'rgba(22,163,74,0.32)',
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.06), inset 0 -1px 0 rgba(255,255,255,0.06)',
  },
  rsvpWarning: {
    background: 'rgba(161,90,0,0.09)',
    borderColor: 'rgba(161,90,0,0.26)',
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.06), inset 0 -1px 0 rgba(255,255,255,0.06)',
  },
  rsvpDanger: {
    background: 'rgba(160,24,24,0.09)',
    borderColor: 'rgba(160,24,24,0.24)',
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.06), inset 0 -1px 0 rgba(255,255,255,0.06)',
  },
  rsvpBadgeText: {
    fontSize: 9,
    fontWeight: 700,
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  addedNote: {
    marginTop: 6,
    fontSize: 10,
    color: '#6B6358',
  },
  partyText: {
    fontSize: 12.5,
    fontWeight: 600,
    color: '#463F34',
  },
  actionButton: {
    width: 36,
    height: 36,
    borderRadius: 12,
    border: '1.5px solid rgba(198,167,90,0.50)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: theme.bgCard,
    cursor: 'pointer',
    boxShadow: '0px 2px 8px rgba(18,18,18,0.08), inset 0px 1px 0px rgba(255,255,255,0.94)',
  },
  
  // Journey Cell
  journeyCard: {
    position: 'relative',
    borderRadius: 14,
    border: '1px solid rgba(198,167,90,0.35)',
    background: COLORS.cream,
    padding: '8px 10px',
    minWidth: 260,
    boxShadow: '0px 2px 8px rgba(23,19,13,0.08)',
  },
  journeyEmptyCard: {
    minHeight: 74,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  journeyEmptyText: {
    marginTop: 6,
    fontSize: 10.5,
    color: COLORS.textMuted,
    lineHeight: 1.4,
  },
  journeyMetaRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  journeyMeta: {
    fontSize: 10,
    fontWeight: 700,
    color: COLORS.textLight,
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  journeyNextStep: {
    fontSize: 10,
    fontWeight: 600,
    color: '#463F34',
  },
  journeyRail: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  journeyDotBtn: {
    position: 'relative',
    width: 17,
    height: 17,
    borderRadius: 8.5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: theme.bgCard,
    border: '1.1px solid rgba(198,167,90,0.30)',
    cursor: 'pointer',
    boxShadow: '0px 6px 10px rgba(18,18,18,0.04), inset 0px 1px 0px rgba(255,255,255,0.92)',
  },
  journeyDot: {
    width: 9,
    height: 9,
    borderRadius: 4.5,
    background: '#E6E0D4',
    border: '1px solid rgba(198,167,90,0.30)',
  },
  journeyDotActive: {
    background: '#2ED47A',
    border: '1px solid #2ED47A',
  },
  journeyDotManual: {
    background: '#6BBF8A',
    border: '1px solid #6BBF8A',
  },
  journeyDotOverdue: {
    background: '#E53E3E',
    border: '1px solid #E53E3E',
  },
  journeyDotWarning: {
    background: '#F0B429',
    border: '1px solid #F0B429',
  },
  journeyDotFailed: {
    fontSize: 10,
    fontWeight: 700,
    color: '#C36A6A',
    lineHeight: 1,
  },
  journeySourceIcon: {
    position: 'absolute',
    right: -6,
    bottom: -6,
    width: 12,
    height: 12,
    borderRadius: 6,
    border: '1.5px solid rgba(198,167,90,0.55)',
    background: COLORS.cream,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  journeyLabelsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 7,
    gap: 7,
  },
  journeyStepLabel: {
    fontSize: 9,
    fontWeight: 700,
    color: '#7A7060',
    letterSpacing: 0.15,
  },
  journeyStepLabelActive: {
    color: '#2ED47A',
  },
  journeyStepLabelFailed: {
    color: COLORS.danger,
  },
  
  // Alert Cell
  alertCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 34,
    width: '100%',
  },
  alertCountOrb: {
    width: 28,
    height: 22,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(185,28,28,0.08)',
    border: '1px solid rgba(185,28,28,0.22)',
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.08), inset 0 -1px 0 rgba(255,255,255,0.06)',
  },
  alertCountOrbNeutral: {
    background: 'rgba(139,130,116,0.08)',
    border: '1px solid rgba(139,130,116,0.20)',
  },
  alertCountText: {
    color: '#B91C1C',
    fontSize: 10,
    fontWeight: 700,
  },
  alertLifecycleBadge: {
    borderRadius: 999,
    border: '1.5px solid rgba(198,167,90,0.55)',
    background: theme.bgCard,
    padding: '5px 8px',
    minWidth: 118,
    textAlign: 'center',
  },
  alertLifecycleText: {
    fontSize: 9,
    fontWeight: 700,
    color: '#8B6A1A',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  alertIssueWarning: {
    background: theme.statusWarningTint,
    borderColor: 'rgba(198,167,90,0.28)',
  },
  alertIssueDanger: {
    background: theme.statusErrorTint,
    borderColor: 'rgba(214,119,119,0.32)',
  },
  alertIssueNeutral: {
    background: theme.bgCard,
    borderColor: 'rgba(181,173,160,0.34)',
  },
  
  // Modal styles
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(20,18,14,0.42)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 18,
    zIndex: 1000,
  },
  modalCard: {
    width: '100%',
    maxWidth: 520,
    borderRadius: 24,
    background: COLORS.cream,
    border: `1px solid ${COLORS.border}`,
    borderTop: `2px solid ${COLORS.gold}`,
    padding: 22,
    boxShadow: '0px 36px 74px rgba(18,18,18,0.16), 0px 12px 28px rgba(18,18,18,0.08)',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  modalCardWide: {
    maxWidth: 760,
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 18,
    paddingBottom: 14,
    borderBottom: '1px solid rgba(198,167,90,0.12)',
  },
  modalEyebrow: {
    fontSize: 10,
    fontWeight: 700,
    color: COLORS.textLight,
    textTransform: 'uppercase',
    letterSpacing: 0.9,
    marginBottom: 6,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: COLORS.text,
    margin: 0,
  },
  modalSubtitle: {
    marginTop: 4,
    fontSize: 11.5,
    color: COLORS.textMuted,
    lineHeight: 1.5,
  },
  modalCloseBtn: {
    width: 32,
    height: 32,
    borderRadius: 10,
    border: '1px solid rgba(198,167,90,0.18)',
    background: COLORS.cream,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0px 10px 18px rgba(18,18,18,0.03)',
  },
  modalPrimaryBtn: {
    marginTop: 14,
    borderRadius: 12,
    background: COLORS.gold,
    padding: 12,
    minWidth: 140,
    border: 'none',
    fontSize: 13,
    fontWeight: 700,
    color: '#fff',
    cursor: 'pointer',
    boxShadow: '0px 14px 24px rgba(155,116,32,0.18)',
  },
  modalPrimaryBtnDisabled: {
    background: '#EDE8DD',
    cursor: 'not-allowed',
  },
  modalSecondaryBtn: {
    flex: 1,
    borderRadius: 12,
    border: `1px solid ${COLORS.border}`,
    background: COLORS.cream,
    padding: 12,
    fontSize: 13,
    fontWeight: 700,
    color: '#463F34',
    cursor: 'pointer',
    boxShadow: '0px 10px 18px rgba(18,18,18,0.03)',
  },
  modalActionRow: {
    marginTop: 14,
    display: 'flex',
    gap: 10,
  },
  
  // Upload well
  uploadWell: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '13px 14px',
    borderRadius: 14,
    border: '1px dashed rgba(198,167,90,0.34)',
    background: theme.statusWarningTint,
    marginBottom: 14,
    cursor: 'pointer',
  },
  uploadWellText: {
    fontSize: 13,
    fontWeight: 700,
    color: '#463F34',
  },
  modalFieldLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: COLORS.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.75,
    marginBottom: 8,
  },
  modalTextarea: {
    width: '100%',
    height: 128,
    borderRadius: 14,
    border: '1px solid rgba(226,220,207,0.84)',
    background: theme.bgTableDivider,
    padding: 12,
    fontSize: 12,
    color: COLORS.text,
    fontFamily: 'monospace',
    resize: 'vertical',
    boxShadow: 'inset 0px 1px 0px rgba(255,255,255,0.82)',
  },
  
  // Preview table
  previewTable: {
    maxHeight: 240,
    borderRadius: 14,
    border: '1px solid rgba(198,167,90,0.18)',
    background: theme.bgCard,
    overflow: 'auto',
  },
  previewHeader: {
    display: 'flex',
    background: theme.bgCard,
    padding: '9px 10px',
    position: 'sticky',
    top: 0,
  },
  previewHeaderText: {
    flex: 1,
    fontSize: 10,
    fontWeight: 700,
    color: COLORS.textMuted,
    textTransform: 'uppercase',
  },
  previewRow: {
    display: 'flex',
    padding: '9px 10px',
    borderTop: '1px solid rgba(198,167,90,0.12)',
  },
  previewCell: {
    flex: 1,
    fontSize: 11,
    color: '#463F34',
  },
  
  // Confirm / Result cards
  confirmCard: {
    padding: 18,
    borderRadius: 14,
    border: '1.5px solid rgba(198,167,90,0.55)',
    background: theme.accentTintLight,
    textAlign: 'center',
    boxShadow: '0px 16px 30px rgba(18,18,18,0.035)',
  },
  confirmCount: {
    fontSize: 30,
    fontWeight: 800,
    color: COLORS.text,
  },
  confirmLabel: {
    fontSize: 12,
    color: COLORS.textMuted,
    marginTop: 3,
  },
  confirmNote: {
    marginTop: 8,
    fontSize: 11,
    color: '#9b9385',
  },
  resultCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '18px',
    borderRadius: 14,
    border: '1px solid rgba(198,167,90,0.18)',
    background: theme.accentTintLight,
    marginBottom: 14,
  },
  resultTitle: {
    marginTop: 10,
    fontSize: 18,
    fontWeight: 700,
    color: COLORS.text,
  },
  resultSubtitle: {
    marginTop: 6,
    fontSize: 13,
    color: COLORS.textMuted,
  },
  
  // Empty state
  emptyState: {
    padding: '48px 24px',
    textAlign: 'center',
    borderRadius: 14,
    border: `1px solid ${COLORS.border}`,
    background: COLORS.cream,
  },
  emptyText: {
    marginTop: 16,
    fontSize: 16,
    fontWeight: 600,
    color: COLORS.text,
  },
  emptySubtext: {
    marginTop: 8,
    fontSize: 13,
    color: COLORS.textMuted,
    lineHeight: 1.5,
  },
  
  // Avatar menu
  avatarMenuCard: {
    width: '100%',
    maxWidth: 320,
    borderRadius: 14,
    border: '1px solid rgba(198,167,90,0.26)',
    background: COLORS.cream,
    padding: 14,
    boxShadow: '0px 14px 20px rgba(23,19,13,0.16)',
  },
  avatarMenuTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: COLORS.textLight,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 10,
  },
  avatarMenuAction: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    borderRadius: 10,
    border: '1.5px solid rgba(198,167,90,0.55)',
    background: theme.bgCard,
    padding: '10px',
    marginBottom: 8,
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
    color: '#463F34',
  },
  avatarMenuDanger: {
    borderColor: 'rgba(178,78,78,0.24)',
    background: theme.statusErrorTint,
    color: COLORS.danger,
  },
  
  // Journey config styles
  journeyConfigBlock: {
    borderRadius: 14,
    border: '1px solid rgba(198,167,90,0.18)',
    background: theme.bgCard,
    padding: 14,
    marginBottom: 16,
  },
  configSectionEyebrow: {
    fontSize: 10,
    fontWeight: 700,
    color: COLORS.textLight,
    textTransform: 'uppercase',
    letterSpacing: 0.9,
  },
  configSectionIntro: {
    marginTop: 6,
    fontSize: 11,
    color: COLORS.textMuted,
    lineHeight: 1.45,
  },
  templateChoiceList: {
    marginTop: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  templateChoiceRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    borderRadius: 12,
    border: '1px solid rgba(198,167,90,0.18)',
    background: theme.bgCard,
    padding: '11px 12px',
    cursor: 'pointer',
  },
  templateChoiceRowActive: {
    background: theme.statusWarningTint,
    borderColor: 'rgba(198,167,90,0.30)',
  },
  choiceCheckbox: {
    width: 18,
    height: 18,
    borderRadius: 6,
    border: '1px solid rgba(198,167,90,0.34)',
    background: theme.bgCard,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  choiceCheckboxActive: {
    background: COLORS.gold,
    borderColor: COLORS.gold,
  },
  templateChoiceTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: COLORS.text,
  },
  templateChoiceMeta: {
    marginTop: 3,
    fontSize: 10.5,
    color: COLORS.textMuted,
  },
  suggestedBadge: {
    borderRadius: 999,
    padding: '4px 8px',
    background: theme.statusWarningTint,
    fontSize: 9,
    fontWeight: 700,
    color: COLORS.goldDark,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginLeft: 8,
  },
  selectedJourneyList: {
    marginTop: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  selectedJourneyRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    borderRadius: 12,
    border: '1px solid rgba(198,167,90,0.18)',
    background: theme.bgCard,
    padding: '11px 12px',
  },
  selectedJourneyOrder: {
    width: 24,
    height: 24,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#F3EEE2',
    fontSize: 10,
    fontWeight: 700,
    color: COLORS.goldDark,
    flexShrink: 0,
  },
  shortLabelInput: {
    width: 72,
    borderRadius: 10,
    border: '1px solid rgba(198,167,90,0.24)',
    background: theme.bgCard,
    padding: '9px 10px',
    fontSize: 11,
    fontWeight: 700,
    color: '#463F34',
    textAlign: 'center',
    textTransform: 'uppercase',
  },
  reorderButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  reorderBtn: {
    width: 28,
    height: 28,
    borderRadius: 10,
    border: '1px solid rgba(198,167,90,0.18)',
    background: COLORS.cream,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  reorderBtnDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
};

// ─── Select Menu Component ─────────────────────────────────────────────────────
function SelectMenu({ label, value, options, onSelect }) {
  const [open, setOpen] = useState(false);
  const active = options.find(o => o.value === value);
  
  return (
    <div style={styles.selectPill} onClick={() => setOpen(!open)}>
      <span style={styles.selectLabel}>{label}</span>
      <span style={styles.selectValue}>{active?.label || value}</span>
      <ChevronDown size={13} color={COLORS.textLight} />
      {open && (
        <div style={styles.selectDropdown} onClick={e => e.stopPropagation()}>
          {options.map((opt, idx) => (
            <div
              key={opt.value}
              style={{
                ...styles.selectMenuItem,
                ...(idx === 0 ? styles.selectMenuItemFirst : {}),
              }}
              onClick={() => { onSelect(opt.value); setOpen(false); }}
            >
              <span style={{
                ...styles.selectMenuText,
                ...(value === opt.value ? styles.selectMenuTextActive : {}),
              }}>
                {opt.label}
              </span>
              {value === opt.value && <Check size={14} color={COLORS.gold} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── CSV Import Modal ──────────────────────────────────────────────────────────
function CsvImportModal({ visible, onClose, onImport }) {
  const [stage, setStage] = useState('upload');
  const [csvText, setCsvText] = useState('');
  const [rows, setRows] = useState([]);
  const [parseWarnings, setParseWarnings] = useState([]);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  
  const reset = () => {
    setStage('upload');
    setCsvText('');
    setRows([]);
    setParseWarnings([]);
    setResult(null);
  };
  
  const handleClose = () => {
    reset();
    onClose();
  };
  
  const handleParse = () => {
    const { rows: parsed, warnings } = parseCsvText(csvText);
    if (parsed.length === 0) {
      alert('No valid rows found. Make sure your CSV has at least a first name column.');
      return;
    }
    setRows(parsed);
    setParseWarnings(warnings || []);
    setStage('preview');
  };
  
  const handleConfirm = async () => {
    setImporting(true);
    try {
      const response = await onImport(rows);
      setResult(response);
      setStage('result');
    } catch {
      alert('Import failed. Please try again.');
    } finally {
      setImporting(false);
    }
  };
  
  const pickFile = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,text/csv';
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => setCsvText(ev.target?.result || '');
      reader.readAsText(file);
    };
    input.click();
  };
  
  // Count guests missing contact info
  const missingContactCount = rows.filter(r => !r._has_contact).length;
  
  if (!visible) return null;
  
  return (
    <div style={styles.modalOverlay} onClick={handleClose}>
      <div style={{ ...styles.modalCard, maxWidth: 640 }} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <div>
            <div style={styles.modalEyebrow}>Guest Import</div>
            <h3 style={styles.modalTitle}>Import Guests</h3>
            <p style={styles.modalSubtitle}>
              {stage === 'upload' ? 'Step 1 of 3 — Upload or paste your CSV' :
               stage === 'preview' ? `Step 2 of 3 — Review ${rows.length} guests` :
               stage === 'confirm' ? `Step 3 of 3 — Confirm ${rows.length} guests` :
               'Import complete'}
            </p>
          </div>
          <button style={styles.modalCloseBtn} onClick={handleClose}>
            <X size={18} color={COLORS.textMuted} />
          </button>
        </div>
        
        {stage === 'upload' && (
          <>
            {/* Friendly instruction block */}
            <div style={{
              background: 'rgba(198,167,90,0.08)',
              border: '1px solid rgba(198,167,90,0.2)',
              borderRadius: 10,
              padding: 16,
              marginBottom: 20,
            }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, marginBottom: 10 }}>
                CSV Format Guide
              </div>
              <div style={{ fontSize: 13, color: COLORS.textSecondary, lineHeight: 1.6 }}>
                <div style={{ marginBottom: 8 }}>
                  <strong style={{ color: COLORS.gold }}>Required:</strong> First Name
                </div>
                <div style={{ marginBottom: 8 }}>
                  <strong style={{ color: COLORS.textMuted }}>Optional:</strong> Last Name, Email, Phone, Party/Household
                </div>
                <div style={{ marginBottom: 12 }}>
                  <strong>Accepted headers:</strong>
                  <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 4, fontFamily: 'monospace' }}>
                    First, first_name, FirstName · Last, last_name, Surname · Email, email · Phone, Mobile, Cell
                  </div>
                </div>
                <div style={{
                  background: COLORS.surface,
                  padding: 10,
                  borderRadius: 6,
                  fontSize: 12,
                  fontFamily: 'monospace',
                  color: COLORS.textSecondary,
                }}>
                  First,Last,Email,Phone<br/>
                  Jane,Doe,jane@email.com,555-1234<br/>
                  John,Smith,,555-5678<br/>
                  Maria,Garcia,,
                </div>
                <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 10, fontStyle: 'italic' }}>
                  Guests without email or phone will be imported and flagged for follow-up. You can add contact info later.
                </div>
              </div>
            </div>
            
            <div style={styles.uploadWell} onClick={pickFile}>
              <Upload size={18} color={COLORS.gold} />
              <span style={styles.uploadWellText}>Choose CSV file…</span>
              {csvText.length > 0 && <CheckCircle size={16} color={COLORS.success} style={{ marginLeft: 'auto' }} />}
            </div>
            <div style={styles.modalFieldLabel}>Or paste CSV content</div>
            <textarea
              style={styles.modalTextarea}
              value={csvText}
              onChange={e => setCsvText(e.target.value)}
              placeholder="First,Last,Email,Phone&#10;Jane,Doe,jane@example.com,555-1234&#10;John,Smith,,555-5678"
            />
            <button
              style={{
                ...styles.modalPrimaryBtn,
                ...(csvText.trim().length === 0 ? styles.modalPrimaryBtnDisabled : {}),
              }}
              disabled={csvText.trim().length === 0}
              onClick={handleParse}
            >
              Parse & Preview
            </button>
          </>
        )}
        
        {stage === 'preview' && (
          <>
            {/* Warning banner for missing contact info */}
            {missingContactCount > 0 && (
              <div style={{
                background: 'rgba(245,158,11,0.1)',
                border: '1px solid rgba(245,158,11,0.3)',
                borderRadius: 8,
                padding: 12,
                marginBottom: 16,
                fontSize: 13,
                color: '#92400e',
              }}>
                <strong>{missingContactCount} guest{missingContactCount > 1 ? 's' : ''}</strong> without email or phone will be imported and flagged for follow-up.
              </div>
            )}
            
            {parseWarnings.length > 0 && (
              <div style={{
                background: 'rgba(220,38,38,0.08)',
                border: '1px solid rgba(220,38,38,0.2)',
                borderRadius: 8,
                padding: 12,
                marginBottom: 16,
                fontSize: 12,
                color: '#991b1b',
              }}>
                {parseWarnings.slice(0, 5).map((w, i) => <div key={i}>{w}</div>)}
                {parseWarnings.length > 5 && <div>...and {parseWarnings.length - 5} more</div>}
              </div>
            )}
            
            <div style={styles.previewTable}>
              <div style={styles.previewHeader}>
                {['First', 'Last', 'Email', 'Phone', ''].map(h => (
                  <span key={h} style={styles.previewHeaderText}>{h}</span>
                ))}
              </div>
              {rows.slice(0, 20).map((row, idx) => (
                <div key={idx} style={{
                  ...styles.previewRow,
                  background: !row._has_contact ? 'rgba(245,158,11,0.08)' : undefined,
                }}>
                  <span style={styles.previewCell}>{row.first_name}</span>
                  <span style={styles.previewCell}>{row.last_name || '—'}</span>
                  <span style={{ ...styles.previewCell, color: row.email ? undefined : COLORS.textMuted }}>{row.email || '—'}</span>
                  <span style={{ ...styles.previewCell, color: row.phone ? undefined : COLORS.textMuted }}>{row.phone || '—'}</span>
                  <span style={styles.previewCell}>
                    {!row._has_contact && (
                      <span title="Missing contact info" style={{ color: '#f59e0b', fontSize: 11 }}>⚠</span>
                    )}
                  </span>
                </div>
              ))}
              {rows.length > 20 && (
                <div style={{ padding: 12, textAlign: 'center', color: COLORS.textMuted, fontSize: 13 }}>
                  ...and {rows.length - 20} more rows
                </div>
              )}
            </div>
            <div style={styles.modalActionRow}>
              <button style={styles.modalSecondaryBtn} onClick={() => setStage('upload')}>Back</button>
              <button style={styles.modalPrimaryBtn} onClick={() => setStage('confirm')}>Looks Good</button>
            </div>
          </>
        )}
        
        {stage === 'confirm' && (
          <>
            <div style={styles.confirmCard}>
              <div style={styles.confirmCount}>{rows.length}</div>
              <div style={styles.confirmLabel}>guests ready to import</div>
              <div style={styles.confirmNote}>
                Potential duplicates will be flagged for review, not auto-merged.
                {missingContactCount > 0 && (
                  <span style={{ display: 'block', marginTop: 4, color: '#92400e' }}>
                    {missingContactCount} guest{missingContactCount > 1 ? 's' : ''} will need contact info added later.
                  </span>
                )}
              </div>
            </div>
            <div style={styles.modalActionRow}>
              <button style={styles.modalSecondaryBtn} onClick={() => setStage('preview')}>Back</button>
              <button
                style={{ ...styles.modalPrimaryBtn, background: COLORS.success }}
                onClick={handleConfirm}
                disabled={importing}
              >
                {importing ? 'Importing...' : 'Confirm Import'}
              </button>
            </div>
          </>
        )}
        
        {stage === 'result' && result && (
          <>
            <div style={styles.resultCard}>
              <CheckCircle size={44} color={COLORS.success} />
              <div style={styles.resultTitle}>Import Complete</div>
              <div style={styles.resultSubtitle}>{result.added} added · {result.skipped} skipped</div>
            </div>
            <button style={styles.modalPrimaryBtn} onClick={handleClose}>Done</button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Journey Config Modal ──────────────────────────────────────────────────────
function JourneyConfigModal({ visible, templates, currentConfig, onClose, onSave }) {
  const [draftItems, setDraftItems] = useState([]);
  
  // Default journey steps if no config
  const defaultSteps = [
    { template_id: 'save', short_label: 'SAVE', kind: 'save' },
    { template_id: 'invite', short_label: 'INV', kind: 'invite' },
    { template_id: 'reminder1', short_label: 'R1', kind: 'reminder' },
    { template_id: 'reminder2', short_label: 'R2', kind: 'reminder' },
    { template_id: 'rsvp', short_label: 'RSVP', kind: 'rsvp' },
  ];
  
  useEffect(() => {
    if (!visible) return;
    const items = currentConfig?.items?.length
      ? currentConfig.items.map((item, idx) => ({ ...item, order: idx }))
      : defaultSteps.map((item, idx) => ({ ...item, order: idx }));
    setDraftItems(items);
  }, [visible, currentConfig]);
  
  const selectedSet = new Set(draftItems.map(item => item.template_id));
  
  const toggleTemplate = (templateId, label, kind) => {
    setDraftItems(current => {
      const idx = current.findIndex(item => item.template_id === templateId);
      if (idx >= 0) {
        return current.filter(item => item.template_id !== templateId).map((item, i) => ({ ...item, order: i }));
      }
      return [...current, { template_id: templateId, short_label: label, kind, order: current.length }];
    });
  };
  
  const moveItem = (index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= draftItems.length) return;
    setDraftItems(current => {
      const next = [...current];
      const [item] = next.splice(index, 1);
      next.splice(nextIndex, 0, item);
      return next.map((entry, i) => ({ ...entry, order: i }));
    });
  };
  
  const updateLabel = (templateId, shortLabel) => {
    setDraftItems(current =>
      current.map(item =>
        item.template_id === templateId
          ? { ...item, short_label: shortLabel.toUpperCase().slice(0, 6) }
          : item
      )
    );
  };
  
  const handleSave = () => {
    onSave({
      items: draftItems.map((item, idx) => ({
        template_id: item.template_id,
        order: idx,
        short_label: item.short_label.trim() || 'STEP',
        kind: item.kind,
      })),
      updated_at: new Date().toISOString(),
    });
    onClose();
  };
  
  if (!visible) return null;
  
  const availableTemplates = [
    { id: 'save', name: 'Save the Date', kind: 'save', suggested: true },
    { id: 'invite', name: 'Invitation', kind: 'invite', suggested: true },
    { id: 'reminder1', name: 'Reminder 1', kind: 'reminder', suggested: true },
    { id: 'reminder2', name: 'Reminder 2', kind: 'reminder', suggested: true },
    { id: 'rsvp', name: 'RSVP Confirmation', kind: 'rsvp', suggested: true },
    { id: 'thankyou', name: 'Thank You', kind: 'custom', suggested: false },
  ];
  
  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={{ ...styles.modalCard, ...styles.modalCardWide }} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <div style={{ flex: 1 }}>
            <h3 style={styles.modalTitle}>Configure Journey</h3>
            <p style={styles.modalSubtitle}>Choose the milestone templates to track, set the order, and refine short labels.</p>
          </div>
          <button style={styles.modalCloseBtn} onClick={onClose}>
            <X size={18} color="#463F34" />
          </button>
        </div>
        
        <div style={styles.journeyConfigBlock}>
          <div style={styles.configSectionEyebrow}>Available Deliverables</div>
          <div style={styles.configSectionIntro}>Suggested milestone templates are pre-selected, but you control the final journey.</div>
          <div style={styles.templateChoiceList}>
            {availableTemplates.map(template => {
              const selected = selectedSet.has(template.id);
              return (
                <div
                  key={template.id}
                  style={{
                    ...styles.templateChoiceRow,
                    ...(selected ? styles.templateChoiceRowActive : {}),
                  }}
                  onClick={() => toggleTemplate(template.id, template.name.slice(0, 4).toUpperCase(), template.kind)}
                >
                  <div style={{
                    ...styles.choiceCheckbox,
                    ...(selected ? styles.choiceCheckboxActive : {}),
                  }}>
                    {selected && <Check size={12} color="#fff" />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={styles.templateChoiceTitle}>{template.name}</span>
                      {template.suggested && <span style={styles.suggestedBadge}>Suggested</span>}
                    </div>
                    <div style={styles.templateChoiceMeta}>{template.kind}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        <div style={styles.journeyConfigBlock}>
          <div style={styles.configSectionEyebrow}>Tracked Journey</div>
          <div style={styles.configSectionIntro}>Recommended: 4-6 key milestones for a clean guest journey.</div>
          {draftItems.length === 0 ? (
            <div style={{ marginTop: 12, padding: 16, border: '1px dashed rgba(198,167,90,0.16)', borderRadius: 12, background: theme.bgCard }}>
              <span style={{ fontSize: 11, color: COLORS.textMuted }}>Select at least one template to define the journey.</span>
            </div>
          ) : (
            <div style={styles.selectedJourneyList}>
              {draftItems.map((item, idx) => {
                const template = availableTemplates.find(t => t.id === item.template_id);
                return (
                  <div key={item.template_id} style={styles.selectedJourneyRow}>
                    <div style={styles.selectedJourneyOrder}>{idx + 1}</div>
                    <div style={{ flex: 1 }}>
                      <div style={styles.templateChoiceTitle}>{template?.name || item.template_id}</div>
                      <div style={styles.templateChoiceMeta}>{item.kind}</div>
                    </div>
                    <input
                      style={styles.shortLabelInput}
                      value={item.short_label}
                      onChange={e => updateLabel(item.template_id, e.target.value)}
                      placeholder="LABEL"
                      maxLength={6}
                    />
                    <div style={styles.reorderButtons}>
                      <button
                        style={{ ...styles.reorderBtn, ...(idx === 0 ? styles.reorderBtnDisabled : {}) }}
                        onClick={() => moveItem(idx, -1)}
                        disabled={idx === 0}
                      >
                        <ChevronUp size={13} color={idx === 0 ? '#C1B7A6' : COLORS.goldDark} />
                      </button>
                      <button
                        style={{ ...styles.reorderBtn, ...(idx === draftItems.length - 1 ? styles.reorderBtnDisabled : {}) }}
                        onClick={() => moveItem(idx, 1)}
                        disabled={idx === draftItems.length - 1}
                      >
                        <ChevronDown size={13} color={idx === draftItems.length - 1 ? '#C1B7A6' : COLORS.goldDark} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        <div style={styles.modalActionRow}>
          <button style={styles.modalSecondaryBtn} onClick={onClose}>Cancel</button>
          <button
            style={{
              ...styles.modalPrimaryBtn,
              ...(draftItems.length === 0 ? styles.modalPrimaryBtnDisabled : {}),
            }}
            onClick={handleSave}
            disabled={draftItems.length === 0}
          >
            Save Journey
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Avatar Menu Modal ─────────────────────────────────────────────────────────
function AvatarMenuModal({ visible, guest, onClose, onUpload, onRemove }) {
  if (!visible || !guest) return null;
  
  const handleUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        onUpload(guest.id, ev.target?.result);
        onClose();
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };
  
  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.avatarMenuCard} onClick={e => e.stopPropagation()}>
        <div style={styles.avatarMenuTitle}>Profile Image</div>
        <div style={styles.avatarMenuAction} onClick={handleUpload}>
          <ImageIcon size={14} color={COLORS.goldDark} />
          <span>Upload / Replace</span>
        </div>
        <div style={styles.avatarMenuAction} onClick={handleUpload}>
          <Camera size={14} color={COLORS.goldDark} />
          <span>Use Camera</span>
        </div>
        <div
          style={{ ...styles.avatarMenuAction, ...styles.avatarMenuDanger }}
          onClick={() => { onRemove(guest.id); onClose(); }}
        >
          <Trash2 size={14} color={COLORS.danger} />
          <span>Remove Image</span>
        </div>
      </div>
    </div>
  );
}

// ─── Guest Journey Cell ────────────────────────────────────────────────────────
function GuestJourneyCell({ guest, journeySteps = [], completionsMap = {}, onToggleStep, onShowTooltip, onHideTooltip }) {
  // If no journey steps configured in backend, show info message
  const hasRealJourney = journeySteps && journeySteps.length > 0;
  
  // Map backend journey steps to display items
  const items = hasRealJourney 
    ? journeySteps.map(step => ({
        template_id: step._id || step.id,
        short_label: step.name?.substring(0, 12) || step.type || 'Step',
        kind: step.type || 'custom',
        name: step.name,
      }))
    : []; // No fallback placeholders - show empty state instead
  
  // Derive step states with stoplight logic
  const getStepState = (item) => {
    const stepCompletions = completionsMap[item.template_id];
    if (stepCompletions && stepCompletions.has(guest.id)) {
      return { state: 'completed', source: 'manual', timestamp: null };
    }
    const statuses = guest.statuses || {};
    const statusKey = item.kind === 'save_the_date' || item.kind === 'save' ? 'std' :
                      item.kind === 'invitation' || item.kind === 'invite' ? 'ir' :
                      item.kind === 'reminder' ? 'r1' :
                      item.kind === 'rsvp' ? 'oi' : null;
    if (statusKey && statuses[statusKey]?.value) {
      return { state: 'completed', source: statuses[statusKey].source || 'system', timestamp: statuses[statusKey].timestamp };
    }
    if ((item.kind === 'invitation' || item.kind === 'invite') && guest.invitation_status === 'sent') {
      return { state: 'completed', source: 'system', timestamp: guest.invitation_sent_at };
    }
    if ((item.kind === 'invitation' || item.kind === 'invite') && guest.invitation_status === 'failed') {
      return { state: 'failed', source: 'system', timestamp: guest.invitation_sent_at };
    }
    if (item.kind === 'rsvp' && rsvpIs(guest, 'yes', 'confirmed')) {
      return { state: 'completed', source: 'system', timestamp: guest.rsvp_responded_at };
    }
    if (item.kind === 'rsvp' && rsvpIs(guest, 'no', 'declined')) {
      return { state: 'failed', source: 'system', timestamp: guest.rsvp_responded_at };
    }
    // Stoplight: check due date for pending steps
    const stepDef = journeySteps.find(s => (s._id || s.id) === item.template_id);
    const dueDate = stepDef?.target_date || stepDef?.due_date;
    if (dueDate) {
      const due = new Date(dueDate);
      const now = new Date();
      const daysUntil = (due - now) / (1000 * 60 * 60 * 24);
      if (daysUntil < 0) return { state: 'overdue', source: null, timestamp: null };
      if (daysUntil <= 7) return { state: 'warning', source: null, timestamp: null };
    }
    return { state: 'pending', source: null, timestamp: null };
  };
  
  // If no journey configured, show info message
  if (!hasRealJourney) {
    return (
      <div style={{ color: COLORS.textMuted, fontSize: 12, fontStyle: 'italic' }}>
        No journey configured
      </div>
    );
  }
  
  const steps = items.map(item => ({
    ...item,
    ...getStepState(item),
  }));
  
  const deliveredCount = steps.filter(s => s.state === 'completed').length;
  const nextPending = steps.find(s => s.state === 'pending');
  const nextLabel = nextPending ? `Next: ${nextPending.short_label}` : 'Journey complete';
  
  const handleDotHover = (e, step) => {
    if (!onShowTooltip) return;
    const rect = e.currentTarget.getBoundingClientRect();
    onShowTooltip({
      type: 'journey',
      x: rect.left + rect.width / 2,
      y: rect.top,
      data: {
        step,
        guest,
      },
    });
  };
  
  if (items.length === 0) {
    return (
      <div style={{ ...styles.journeyCard, ...styles.journeyEmptyCard }}>
        <span style={styles.journeyMeta}>No journey configured</span>
        <span style={styles.journeyEmptyText}>Configure journey in Messaging tab.</span>
      </div>
    );
  }
  
  return (
    <div style={styles.journeyCard}>
      <div style={styles.journeyMetaRow}>
        {hasRealJourney ? (
          <>
            <span style={styles.journeyMeta}>{deliveredCount}/{steps.length} delivered</span>
            <span style={styles.journeyNextStep}>{nextLabel}</span>
          </>
        ) : (
          <span style={{ ...styles.journeyMeta, fontStyle: 'italic' }}>No journey set</span>
        )}
      </div>
      
      <div style={styles.journeyRail}>
        {steps.map((step, idx) => (
          <div
            key={step.template_id}
            style={styles.journeyDotBtn}
            onClick={() => hasRealJourney && onToggleStep && onToggleStep(guest.id, step)}
            onMouseEnter={(e) => hasRealJourney && handleDotHover(e, step)}
            onMouseLeave={() => onHideTooltip && onHideTooltip()}
            title={hasRealJourney ? undefined : step.short_label}
          >
            {step.state === 'failed' ? (
              <span style={styles.journeyDotFailed}>×</span>
            ) : step.state === 'neutral' ? (
              <div style={{
                ...styles.journeyDot,
                background: '#E0DCD4',
                border: '1px solid #D0CCC4',
              }} />
            ) : (
              <div style={{
                ...styles.journeyDot,
                ...(step.state === 'completed' && step.source === 'manual' ? styles.journeyDotManual : 
                    step.state === 'completed' ? styles.journeyDotActive : 
                    step.state === 'overdue' ? styles.journeyDotOverdue :
                    step.state === 'warning' ? styles.journeyDotWarning : {}),
              }} />
            )}
            {step.state !== 'pending' && step.state !== 'neutral' && (
              <div style={styles.journeySourceIcon}>
                {step.source === 'manual' ? (
                  <User size={8} color="#7E6C42" />
                ) : (
                  <Zap size={8} color="#8F7A49" />
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      
      <div style={styles.journeyLabelsRow}>
        {steps.map(step => (
          <span
            key={step.template_id}
            style={{
              ...styles.journeyStepLabel,
              ...(step.state === 'completed' ? styles.journeyStepLabelActive : {}),
              ...(step.state === 'failed' ? styles.journeyStepLabelFailed : {}),
              ...(step.state === 'overdue' ? { color: '#E53E3E' } : {}),
              ...(step.state === 'warning' ? { color: '#F0B429' } : {}),
              ...(step.state === 'neutral' ? { color: '#A9A49C' } : {}),
            }}
          >
            {step.short_label}
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── Guest Alert Cell ──────────────────────────────────────────────────────────
function GuestAlertCell({ guest, onShowTooltip, onHideTooltip }) {
  const alerts = getGuestIssues(guest);
  const primaryIssue = alerts[0]?.title || 'No Issues';
  const hasCritical = alerts.some(a => a.severity === 'critical');
  
  const handleHover = (e) => {
    if (!onShowTooltip || alerts.length === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    onShowTooltip({
      type: 'alert',
      x: rect.left + rect.width / 2,
      y: rect.top,
      data: { alerts, guest },
    });
  };
  
  const severityStyle = hasCritical
    ? styles.alertIssueDanger
    : alerts.length > 0
    ? styles.alertIssueWarning
    : styles.alertIssueNeutral;
  
  return (
    <div 
      style={styles.alertCell}
      onMouseEnter={handleHover}
      onMouseLeave={() => onHideTooltip && onHideTooltip()}
    >
      {/* Only show alert content if there are actual issues */}
      {alerts.length > 0 ? (
        <div style={{ ...styles.alertCountOrb, ...(hasCritical ? {} : styles.alertCountOrbNeutral) }}>
          <span style={{ ...styles.alertCountText, ...(hasCritical ? {} : { color: '#7A7060' }) }}>
            {alerts.length}
          </span>
        </div>
      ) : null}
    </div>
  );
}

// ─── Tooltip Portal Component ──────────────────────────────────────────────────
function TooltipPortal({ tooltip }) {
  if (!tooltip) return null;
  
  const formatTimestamp = (ts) => {
    if (!ts) return 'Not recorded';
    return new Date(ts).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };
  
  const top = Math.max(8, tooltip.y - 8);
  const left = Math.max(8, tooltip.x - 110);
  
  if (tooltip.type === 'journey') {
    const { step, guest } = tooltip.data;
    const statusLabel = step.state === 'completed' ? 'Completed' : step.state === 'failed' ? 'Failed' : 'Pending';
    const sourceLabel = step.source === 'manual' ? 'Manual (Admin)' : step.source === 'system' ? 'System (Auto)' : '—';
    
    return (
      <div style={{ ...styles.tooltipPortal, top, left, transform: 'translateY(-100%)' }}>
        <div style={styles.tooltipCard}>
          <div style={styles.tooltipEyebrow}>Journey Step</div>
          <div style={styles.tooltipTitle}>{step.short_label}</div>
          <div style={styles.tooltipMeta}>{step.kind} milestone</div>
          <div style={styles.tooltipRow}>
            <span style={styles.tooltipRowLabel}>Status</span>
            <span style={styles.tooltipRowValue}>{statusLabel}</span>
          </div>
          <div style={styles.tooltipRow}>
            <span style={styles.tooltipRowLabel}>Source</span>
            <span style={styles.tooltipRowValue}>{sourceLabel}</span>
          </div>
          <div style={styles.tooltipRow}>
            <span style={styles.tooltipRowLabel}>Timestamp</span>
            <span style={styles.tooltipRowValue}>{formatTimestamp(step.timestamp)}</span>
          </div>
        </div>
      </div>
    );
  }
  
  if (tooltip.type === 'alert') {
    const { alerts } = tooltip.data;
    
    return (
      <div style={{ ...styles.tooltipPortal, top, left, transform: 'translateY(-100%)' }}>
        <div style={styles.tooltipCard}>
          <div style={styles.tooltipEyebrow}>Guest Issues</div>
          <div style={styles.tooltipTitle}>{alerts.length} Issue{alerts.length !== 1 ? 's' : ''}</div>
          {alerts.map(alert => (
            <div key={alert.key} style={styles.tooltipIssueRow}>
              <div style={styles.tooltipIssueTitle}>{alert.title}</div>
              <div style={styles.tooltipIssueDesc}>{alert.description}</div>
              <div style={styles.tooltipIssueGuidance}>{alert.guidance}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  return null;
}

// ─── Main GuestListTab Component ───────────────────────────────────────────────
const GuestListTab = ({
  guests = [],
  templates = [],
  eventConfig = {},
  externalSearch = null,
  onAddGuest,
  onUpdateGuest,
  onDeleteGuest,
  onImportGuests,
  onSaveJourneyConfig,
  onUpdateRsvp,
  onNavigateToTab,
  refresh,
  focusGuestId = null,
  onFocusGuestConsumed,
  deepLinkEntity,
  setGuests,
  onGoToNotices,
  integrityCheck = null,
}) => {
  // State
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [sortMode, setSortMode] = useState('recent');
  const [selectedIds, setSelectedIds] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [avatarMenuGuest, setAvatarMenuGuest] = useState(null);
  const [avatarOverrides, setAvatarOverrides] = useState({});
  const [avatarUploading, setAvatarUploading] = useState({});
  const [newGuest, setNewGuest] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [isAddingGuest, setIsAddingGuest] = useState(false);
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const [detailGuest, setDetailGuest] = useState(null);
  const [guestMessageCounts, setGuestMessageCounts] = useState({});
  // Video Guestbook indicators (flag-gated, default OFF → never fetched/rendered in prod)
  const videoGuestbookEnabled = isVideoGuestbookEnabled();
  const [gbIndex, setGbIndex] = useState({});   // guest_id -> {has_video, copy_requested, copy_sent_at, copy_channel}
  useEffect(() => {
    if (!videoGuestbookEnabled) return undefined;
    let live = true;
    axios.get(`${API}/api/guestbook/admin/guest-index`)
      .then(({ data }) => { if (live && data && data.index) setGbIndex(data.index); })
      .catch(() => { /* guestbook not set up / no access — indicators just don't show */ });
    return () => { live = false; };
  }, [videoGuestbookEnabled]);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupPrimaryId, setGroupPrimaryId] = useState('');
  const [groupSaving, setGroupSaving] = useState(false);
  const [groupError, setGroupError] = useState('');
  const [activeMetric, setActiveMetric] = useState(null);
  // ISSUE #3 + #6 (Feb 2026): store full attention items per party (not just IDs)
  // so we can (a) refresh after decline/approve so the badge clears, and
  // (b) hover-tooltip can show the real display_message + requested names.
  const [plusOneItemsByParty, setPlusOneItemsByParty] = useState({});

  // ─── Step 2 — invite-funnel surfacing (party/invite-level only) ────────
  // partiesById carries the 5 token-open tracking fields per party so each
  // row can show "Invite opened / Not opened" + first/last/count tooltip.
  // inviteFilter is consumed once from sessionStorage when the dashboard
  // widget drills into this tab; null means no filter active.
  const [partiesById, setPartiesById] = useState({});
  const [inviteFilter, setInviteFilter] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/parties`)
      .then(r => r.json())
      .then(arr => {
        if (cancelled || !Array.isArray(arr)) return;
        const map = {};
        arr.forEach(p => { if (p && p.id) map[p.id] = p; });
        setPartiesById(map);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    let raw = null;
    try { raw = sessionStorage.getItem('admin_guest_list_invite_filter'); } catch (_) {}
    if (!raw) return;
    try { sessionStorage.removeItem('admin_guest_list_invite_filter'); } catch (_) {}
    const allowed = new Set([
      'invite_not_sent', 'invite_sent_not_opened',
      'invite_opened_not_rsvped', 'invite_opened_and_rsvped',
      'invite_reached_landing_only',
    ]);
    if (allowed.has(raw)) setInviteFilter(raw);
  }, []);

  // Dashboard KPI ring drilldown — same one-shot handoff pattern.
  // Applies to the existing RSVP filter (`filter` state) so guests are
  // immediately scoped to "Pending" or "All" coming from the dashboard.
  useEffect(() => {
    let raw = null;
    try { raw = sessionStorage.getItem('admin_guest_list_rsvp_filter'); } catch (_) {}
    if (!raw) return;
    try { sessionStorage.removeItem('admin_guest_list_rsvp_filter'); } catch (_) {}
    const allowed = new Set(['all', 'yes', 'no', 'pending', 'missing-rsvp']);
    if (allowed.has(raw)) setFilter(raw);
  }, []);

  // Helper — derive party-level invite-open status. Wording rule applied:
  // "Invite opened" / "Not opened" — never "this guest opened".
  const getInviteOpenStatus = useCallback((partyDoc) => {
    if (!partyDoc) return { opened: false, label: 'Not opened' };
    const opened = !!partyDoc.first_opened_at;
    return {
      opened,
      label: opened ? 'Invite opened' : 'Not opened',
      first_opened_at:       partyDoc.first_opened_at || null,
      last_opened_at:        partyDoc.last_opened_at || null,
      open_count:            Number(partyDoc.open_count || 0),
      last_open_route:       partyDoc.last_open_route || null,
      deepest_route_reached: partyDoc.deepest_route_reached || null,
      rsvp_submitted_at:     partyDoc.rsvp_submitted_at || null,
      // Celebration guest-hub link opens — DISTINCT from invite opens above.
      celebration_open_count:      Number(partyDoc.celebration_open_count || 0),
      celebration_first_opened_at: partyDoc.celebration_first_opened_at || null,
      celebration_last_opened_at:  partyDoc.celebration_last_opened_at || null,
    };
  }, []);

  // Helper — does this guest's party match the active inviteFilter?
  // (Filter is party-level; every guest in a matching party passes.)
  const guestMatchesInviteFilter = useCallback((g) => {
    if (!inviteFilter) return true;
    const partyDoc = partiesById[g.party_id];
    if (!partyDoc) return false;
    const opened = !!partyDoc.first_opened_at;
    const rsvped = !!partyDoc.rsvp_submitted_at;
    const sent = !!g.sent_invitation;  // guest-level "sent" — already consumed elsewhere
    switch (inviteFilter) {
      case 'invite_not_sent':            return !sent;
      case 'invite_sent_not_opened':     return  sent && !opened;
      case 'invite_opened_not_rsvped':   return  opened && !rsvped;
      case 'invite_opened_and_rsvped':   return  opened &&  rsvped;
      case 'invite_reached_landing_only':return  opened && !rsvped && partyDoc.deepest_route_reached === 'invite';
      default: return true;
    }
  }, [inviteFilter, partiesById]);
  // -------------------------------------------------------------------------
  // ISSUE #1 (Feb 2026): field-level merge modal state
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [mergeFieldSelections, setMergeFieldSelections] = useState({});
  const [mergeSaving, setMergeSaving] = useState(false);
  const [reviewQueue, setReviewQueue] = useState([]);
  const [reviewIndex, setReviewIndex] = useState(0);
  const [reviewGuests, setReviewGuests] = useState([]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [globalDuplicates, setGlobalDuplicates] = useState([]);
  const [editingPg, setEditingPg] = useState(null);
  const [editPgData, setEditPgData] = useState({});
  const [sendingConfirmationFor, setSendingConfirmationFor] = useState(null);
  // Per-person confirmation control (Jul 2026): guestId -> true means the host
  // chose "Don't send" for that member. Default (absent) = send.
  const [confirmSkip, setConfirmSkip] = useState({});
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuRef = useRef(null);
  const [assigningTokens, setAssigningTokens] = useState(false);

  // ─── Item 5 — Milo Add Guest prefill consumer ────────────────────────────
  // One-shot read from sessionStorage on mount. Validates payload shape
  // strictly. On success: expand the Add Guest card, seed the four-field
  // initial state (first_name, last_name, email, phone), and show the
  // dismissable Milo banner. Operator must still click "Add Guest" in
  // the form to actually save. Milo never auto-submits.
  const [miloPrefillBanner, setMiloPrefillBanner] = useState(null);
  useEffect(() => {
    let raw = null;
    try { raw = sessionStorage.getItem('milo_add_guest_prefill'); } catch (_) {}
    if (!raw) return;
    try { sessionStorage.removeItem('milo_add_guest_prefill'); } catch (_) {}
    let payload = null;
    try { payload = JSON.parse(raw); } catch (_) { return; }
    const valid = (
      payload &&
      payload.action_id === 'guests.open_add_guest_prefilled' &&
      payload.target &&
      payload.target.kind === 'guests_add_guest_prefill' &&
      payload.target.prefill &&
      typeof payload.target.prefill === 'object' &&
      typeof payload.target.prefill.first_name === 'string' &&
      payload.target.prefill.first_name.length > 0 &&
      typeof payload.target.prefill.last_name === 'string' &&
      payload.target.prefill.last_name.length > 0
    );
    if (!valid) {
      setMiloPrefillBanner({
        ok: false,
        title: 'Milo prefill ignored',
        detail: 'The prefill payload was malformed or missing the required first/last name. No fields were changed.',
      });
      return;
    }
    const pf = payload.target.prefill;
    setNewGuest({
      first_name: pf.first_name || '',
      last_name: pf.last_name || '',
      email: pf.email || '',
      // Pass phone through as-is. The form's onChange runs
      // formatPhoneInput on subsequent edits — we don't pre-format
      // here because formatPhoneInput is US-only and would mangle
      // international numbers extracted by Milo.
      phone: pf.phone || '',
    });
    setShowAddForm(true);
    setMiloPrefillBanner({
      ok: true,
      title: 'Milo prepared this view',
      summary: payload.summary,
      first_name: pf.first_name,
      last_name: pf.last_name,
      email: pf.email,
      phone: pf.phone,
      detail:
        'Milo only seeded the form values. Duplicate-detection checks, '
        + 'the Add Guest button, and the actual save remain entirely '
        + 'yours. No guest has been created. Review the values, edit '
        + 'as needed, and click Add Guest when you are ready.',
      runbook_family_id: payload.runbook_family_id,
      runbook_version: payload.runbook_version,
    });
  }, []);
  // -----------------------------------------------------------------------

  useEffect(() => {
    if (!exportMenuOpen) return;
    const handler = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [exportMenuOpen]);

  const handleAssignMissingTokens = async () => {
    if (assigningTokens) return;
    const roster = guests.length;
    if (!window.confirm(`Assign URL tokens to guests that don't have one? This will create a private invitation for each (${roster} guest${roster === 1 ? '' : 's'} in current roster will be checked).`)) return;
    setAssigningTokens(true);
    try {
      const r = await fetch(`${API}/api/guests/bulk-assign-tokens`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed to assign tokens');
      const parts = [
        `${d.assigned} assigned`,
        `${d.skipped_already_had_token} already had tokens`,
      ];
      if (d.skipped_no_identity > 0) parts.push(`${d.skipped_no_identity} couldn't be assigned (missing names)`);
      if (Array.isArray(d.errors) && d.errors.length) parts.push(`${d.errors.length} error(s)`);
      toast.success(parts.join(' · '));
      if (Array.isArray(d.errors) && d.errors.length) {
        // Log details for the host — keeps proportional, no modal, no redesign.
        // eslint-disable-next-line no-console
        console.group('[Assign Missing URL Tokens] errors');
        d.errors.forEach(e => console.warn(`- ${e.first_name || ''} ${e.last_name || ''} (${e.guest_id}): ${e.reason}`));
        console.groupEnd();
      }
      refresh?.();
    } catch (e) {
      toast.error(e.message || 'Failed to assign tokens');
    } finally {
      setAssigningTokens(false);
    }
  };

  // Derive existing groups for typeahead and group modal
  const existingGroups = useMemo(() => {
    // Each party gets a single row. We also track member count and primary name so
    // the typeahead on the profile can disambiguate duplicate party names
    // (e.g. two "Mescallado Household" parties distinguishable by "· 1 member · Henry").
    // We also attach a compact `members` list so the side-profile can render
    // "who else is in my party" without a new API call.
    const groups = {};
    guests.forEach(g => {
      const name = g.party_name || g.group_name;
      if (name && g.party_id) {
        if (!groups[g.party_id]) {
          groups[g.party_id] = {
            name,
            party_id: g.party_id,
            has_primary: false,
            member_count: 0,
            primary_name: '',
            first_member_name: '',
            members: [],
          };
        }
        groups[g.party_id].member_count += 1;
        const fullName = `${g.first_name || ''} ${g.last_name || ''}`.trim();
        if (g.is_primary) {
          groups[g.party_id].has_primary = true;
          groups[g.party_id].primary_name = fullName;
        }
        if (!groups[g.party_id].first_member_name && fullName) {
          groups[g.party_id].first_member_name = fullName;
        }
        groups[g.party_id].members.push({
          id: g.id,
          name: fullName || 'Unknown',
          first_name: g.first_name || '',
          last_name: g.last_name || '',
          is_primary: !!g.is_primary,
          email: g.email || '',
          phone: g.phone || '',
          rsvp_status: g.rsvp_status || '',
          age_group: g.age_group || '',
        });
      }
    });
    // Sort members: primary first, then by name
    Object.values(groups).forEach(grp => {
      grp.members.sort((a, b) => {
        if (a.is_primary !== b.is_primary) return a.is_primary ? -1 : 1;
        return a.name.localeCompare(b.name);
      });
    });
    return Object.values(groups);
  }, [guests]);
  
  // Derive the active detail guest: direct open (chevron) OR search result click (focusGuestId)
  // Always prefer the fresh version from the guests array so UI reflects saved data immediately
  const effectiveDetailGuest = useMemo(() => {
    if (detailGuest) {
      const freshGuest = guests.find(g => (g.id || g._id) === (detailGuest.id || detailGuest._id));
      return freshGuest || detailGuest;
    }
    if (focusGuestId && guests.length > 0) {
      return guests.find(g => (g.id || g._id) === focusGuestId) || null;
    }
    return null;
  }, [detailGuest, focusGuestId, guests]);
  
  // Journey steps from backend
  const [journeySteps, setJourneySteps] = useState([]);
  // Completions map: { step_id: Set of guest_ids } — drives journey dot state
  const [completionsMap, setCompletionsMap] = useState({});

  // Fetch journey steps and their completions
  useEffect(() => {
    const fetchJourneyData = async () => {
      try {
        const stepsRes = await axios.get(`${API}/api/journey/steps`);
        const steps = stepsRes.data || [];
        setJourneySteps(steps);

        // Fetch completions for each step in parallel
        const completionResults = await Promise.all(
          steps.map(s => axios.get(`${API}/api/journey/steps/${s.id}/completions`).catch(() => ({ data: [] })))
        );
        const map = {};
        steps.forEach((s, i) => {
          const records = completionResults[i]?.data || [];
          map[s.id] = new Set(records.map(r => r.guest_id));
        });
        setCompletionsMap(map);
      } catch (err) {
        console.error('Failed to fetch journey steps:', err);
        setJourneySteps([]);
      }
    };
    fetchJourneyData();
  }, []);

  // Fetch per-guest message counts + plus-one flags + review queue
  const fetchReviewData = useCallback(() => {
    fetch(`${API}/api/review-queue`).then(r => r.json()).then(q => {
      setReviewQueue(Array.isArray(q) ? q : []);
    }).catch(() => {});
    fetch(`${API}/api/provisional-guests?status=pending`).then(r => r.json()).then(pgs => {
      const dupes = (Array.isArray(pgs) ? pgs : []).filter(pg => pg.duplicate_flag && pg.status === 'pending');
      setGlobalDuplicates(dupes);
    }).catch(() => {});
  }, []);

  // ISSUE #3 + #6 (Feb 2026): wrap fetch so we can refresh on demand
  // (after AGR decline / approve, after detail-sheet close).
  const fetchPlusOneItems = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/attention-items`);
      const resp = await r.json();
      const all = Array.isArray(resp) ? resp : (resp.items || []);
      // Try to fetch parties so we can enrich with additional_guest_names where present.
      let partiesById = {};
      try {
        const pr = await fetch(`${API}/api/parties`);
        const parr = await pr.json();
        if (Array.isArray(parr)) {
          partiesById = parr.reduce((acc, p) => { if (p.id) acc[p.id] = p; return acc; }, {});
        }
      } catch {}
      const map = {};
      all.filter(i => i.reason_code === 'ADDITIONAL_GUEST_REQUEST' && !i.resolved)
        .forEach(i => {
          const partyDoc = partiesById[i.entity_id] || {};
          const names = ((partyDoc.rsvp_logistics || {}).additional_guest_names || [])
            .map(n => `${n.first_name || ''} ${n.last_name || ''}`.trim()).filter(Boolean);
          map[i.entity_id] = {
            id: i.id || i._id,
            display_message: i.display_message || '',
            requested_count: (partyDoc.rsvp_logistics || {}).requested_additional_guests || null,
            requested_names: names,
          };
        });
      setPlusOneItemsByParty(map);
    } catch {}
  }, []);

  useEffect(() => {
    fetch(`${API}/api/messages/guest-counts`)
      .then(r => r.json())
      .then(d => { if (d && typeof d === 'object') setGuestMessageCounts(d); })
      .catch(() => {});
    // Fetch pending plus-one requests
    fetchPlusOneItems();
    fetchReviewData();
  }, [fetchReviewData, fetchPlusOneItems]);

  // Load provisional guests for current review party
  useEffect(() => {
    const party = reviewQueue[reviewIndex];
    if (!party) { setReviewGuests([]); return; }
    setReviewLoading(true);
    fetch(`${API}/api/provisional-guests/by-party/${party.party_id}`)
      .then(r => r.json())
      .then(pgs => { setReviewGuests(Array.isArray(pgs) ? pgs : []); })
      .catch(() => setReviewGuests([]))
      .finally(() => setReviewLoading(false));
  }, [reviewQueue, reviewIndex]);

  // Provisional guest action handlers
  const handlePgAction = async (pgId, action, extra = {}) => {
    try {
      const opts = { method: 'POST', headers: { 'Content-Type': 'application/json' } };
      if (action === 'approve') {
        await fetch(`${API}/api/provisional-guests/${pgId}/approve`, opts);
        toast.success('Guest approved');
      } else if (action === 'delete') {
        opts.body = JSON.stringify({ note: 'Deleted by host' });
        await fetch(`${API}/api/provisional-guests/${pgId}/delete`, opts);
        toast.success('Guest removed');
      } else if (action === 'merge') {
        opts.body = JSON.stringify({ merge_with_guest_id: extra.mergeWith });
        await fetch(`${API}/api/provisional-guests/${pgId}/merge`, opts);
        toast.success('Guest merged');
      } else if (action === 'dismiss-duplicate') {
        await fetch(`${API}/api/provisional-guests/${pgId}/dismiss-duplicate`, opts);
        toast.success('Duplicate dismissed');
      } else if (action === 'edit') {
        await fetch(`${API}/api/provisional-guests/${pgId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(extra) });
        toast.success('Guest updated');
        setEditingPg(null);
      }
      // Refresh review data
      fetchReviewData();
      refresh?.();
      // Reload current party's provisional guests
      const party = reviewQueue[reviewIndex];
      if (party) {
        const r = await fetch(`${API}/api/provisional-guests/by-party/${party.party_id}`);
        const pgs = await r.json();
        setReviewGuests(Array.isArray(pgs) ? pgs : []);
      }
    } catch (e) { toast.error('Action failed'); }
  };

  const handleSendConfirmation = async (partyId, recipientIds) => {
    if (sendingConfirmationFor) return; // guard against double-click
    setSendingConfirmationFor(partyId);
    try {
      // Per-person control (Jul 2026): recipientIds limits the confirmation to
      // exactly the members the host chose; members toggled to "Don't send"
      // are recorded server-side and receive nothing.
      const r = await fetch(`${API}/api/parties/${partyId}/send-confirmation`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(Array.isArray(recipientIds) ? { recipient_guest_ids: recipientIds } : {}),
      });
      const d = await r.json();
      if (r.ok) {
        toast.success(d.message || 'Confirmation sent');
        // Optimistically remove the sent party from the local queue so the UI
        // transitions immediately, then re-sync from the server.
        setReviewQueue(prev => {
          const next = (Array.isArray(prev) ? prev : []).filter(p => p.party_id !== partyId);
          // Clamp reviewIndex to the new length
          setReviewIndex(i => Math.max(0, Math.min(i, next.length - 1)));
          return next;
        });
        setReviewGuests([]);
        fetchReviewData();
        refresh?.();
      } else {
        toast.error(d.detail || 'Cannot send yet');
      }
    } catch { toast.error('Failed to send confirmation'); }
    finally {
      setSendingConfirmationFor(null);
    }
  };

  // Toggle a journey step completion for a guest (mark or unmark)
  const handleToggleStep = async (guestId, step) => {
    const stepId = step.template_id;
    const isCompleted = completionsMap[stepId]?.has(guestId);
    try {
      if (isCompleted) {
        await axios.delete(`${API}/api/journey/steps/${stepId}/completions`, {
          data: { guest_ids: [guestId] }
        });
        setCompletionsMap(prev => {
          const next = { ...prev, [stepId]: new Set(prev[stepId]) };
          next[stepId].delete(guestId);
          return next;
        });
      } else {
        await axios.post(`${API}/api/journey/steps/${stepId}/completions`, {
          guest_ids: [guestId], method: 'manual', completed_by: 'admin'
        });
        setCompletionsMap(prev => {
          const next = { ...prev, [stepId]: new Set(prev[stepId]) };
          next[stepId].add(guestId);
          return next;
        });
      }
    } catch (err) {
      console.error('Failed to toggle step completion:', err);
    }
  };
  
  // Handle external search
  const effectiveSearch = externalSearch || search;
  const isExternalSearch = !!externalSearch;
  
  // Metrics
  //  PROTECTED KPI LABELS — "Pending" and "Invite Not Sent" measure DIFFERENT things:
  //   · Pending        = has an invitation but hasn't replied Yes/No
  //   · Invite Not Sent = invitation_status === 'pending' (i.e. invitation has not been sent yet)
  //  Previously labelled "Missing RSVP" which caused it to be confused with Pending.
  const metrics = useMemo(() => {
    const attending = guests.filter(g => rsvpIs(g, 'yes', 'confirmed')).length;
    const declined = guests.filter(g => rsvpIs(g, 'no', 'declined')).length;
    const pending = guests.filter(g => !rsvpIs(g, 'yes', 'confirmed', 'no', 'declined')).length;
    const inviteNotSent = guests.filter(g => g.invitation_status === 'pending').length;
    // UNIFICATION (Feb 2026): Adults/Children now derived from each guest's
    // age_group (Path B — same source as Dashboard KPI and plate-cost math).
    // Previously used legacy scalar fields `g.adults`/`g.children` (Path A)
    // which drifted from Dashboard because they were never systematically
    // maintained after the age_group model was introduced.
    const adults = guests.filter(g => (g.age_group || '').toUpperCase() === 'ADULT').length;
    const childrenTotal = guests.filter(g => (g.age_group || '').toUpperCase() === 'CHILD').length;
    const incomplete = guests.filter(g => !g.phone || !g.email).length;
    const responses = attending + declined;
    const responseRate = guests.length > 0 ? Math.round((responses / guests.length) * 100) : 0;
    
    return [
      { label: 'Attending', value: String(attending), filterKey: 'yes' },
      { label: 'Declined', value: String(declined), filterKey: 'no' },
      { label: 'Pending', value: String(pending), filterKey: 'pending', tooltip: 'Has an invitation but has not replied Yes or No yet' },
      { label: 'Invite Not Sent', value: String(inviteNotSent), filterKey: 'missing-rsvp', tooltip: 'Invitation has not been dispatched yet (separate from Pending which tracks RSVP response)' },
      { label: 'Adults', value: String(adults), filterKey: 'has-adults' },
      { label: 'Children', value: String(childrenTotal), filterKey: 'has-children' },
      { label: 'Incomplete', value: String(incomplete), filterKey: 'incomplete' },
      { label: 'Response Rate', value: `${responseRate}%`, filterKey: null },
    ];
  }, [guests]);
  
  // Filter & Sort
  const filteredGuests = useMemo(() => {
    const effectiveFilter = activeMetric || filter;

    let list = guests.filter(guest => {
      const matchesSearch = !effectiveSearch ||
        `${guest.first_name} ${guest.last_name}`.toLowerCase().includes(effectiveSearch.toLowerCase()) ||
        guest.email?.toLowerCase().includes(effectiveSearch.toLowerCase()) ||
        guest.phone?.toLowerCase().includes(effectiveSearch.toLowerCase());
      
      if (!matchesSearch) return false;
      // Step 2 — invite-funnel drill-down filter (party-level).
      if (!guestMatchesInviteFilter(guest)) return false;
      
      if (effectiveFilter === 'all') return true;
      if (effectiveFilter === 'yes') return rsvpIs(guest, 'yes', 'confirmed');
      if (effectiveFilter === 'no') return rsvpIs(guest, 'no', 'declined');
      if (effectiveFilter === 'missing-rsvp') return guest.invitation_status === 'pending';
      if (effectiveFilter === 'pending') return !rsvpIs(guest, 'yes', 'confirmed', 'no', 'declined');
      if (effectiveFilter === 'has-adults') return (guest.age_group || '').toUpperCase() === 'ADULT';
      if (effectiveFilter === 'has-children') return (guest.age_group || '').toUpperCase() === 'CHILD';
      if (effectiveFilter === 'incomplete') return !guest.phone || !guest.email;
      return true;
    });
    
    return list.sort((a, b) => {
      // ISSUE #5 (Feb 2026): split single Name sort into First and Last.
      if (sortMode === 'first') return (a.first_name || '').localeCompare(b.first_name || '');
      if (sortMode === 'last') return (a.last_name || '').localeCompare(b.last_name || '');
      // Backward-compat: legacy 'name' = sort by full name string
      if (sortMode === 'name') return `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
      if (sortMode === 'rsvp') {
        const ra = (a.rsvp_status || '').toLowerCase() || 'zzz';
        const rb = (b.rsvp_status || '').toLowerCase() || 'zzz';
        return ra.localeCompare(rb);
      }
      if (sortMode === 'group') return (a.group_name || a.party_name || a.household || 'zzz').localeCompare(b.group_name || b.party_name || b.household || 'zzz');
      return (b.created_at || '').localeCompare(a.created_at || '');
    });
  }, [guests, effectiveSearch, filter, sortMode, activeMetric, guestMatchesInviteFilter, partiesById, inviteFilter]);
  
  const allVisibleSelected = filteredGuests.length > 0 && filteredGuests.every(g => selectedIds.includes(g.id));
  const selectedCount = selectedIds.length;
  
  // Tooltip handlers
  const showTooltip = (data) => setTooltip(data);
  const hideTooltip = () => setTooltip(null);
  
  // Handlers
  const toggleGuest = (guestId) => {
    setSelectedIds(current =>
      current.includes(guestId)
        ? current.filter(id => id !== guestId)
        : [...current, guestId]
    );
  };
  
  const handleAdd = async () => {
    if (!newGuest.first_name || !newGuest.last_name) {
      alert('First name and last name are required.');
      return;
    }
    setIsAddingGuest(true);
    try {
      await onAddGuest?.(newGuest);
      setNewGuest({ first_name: '', last_name: '', email: '', phone: '' });
      setShowAddForm(false);
      refresh?.();
    } catch (err) {
      alert('Failed to add guest.');
    } finally {
      setIsAddingGuest(false);
    }
  };
  
  const handleImport = async (rows) => {
    if (onImportGuests) {
      return await onImportGuests(rows);
    }
    // Fallback: add guests one by one
    let added = 0;
    let skipped = 0;
    for (const row of rows) {
      try {
        await onAddGuest?.(row);
        added++;
      } catch {
        skipped++;
      }
    }
    refresh?.();
    return { added, skipped };
  };
  
  const handleAvatarUpload = (guestId, dataUrl) => {
    setAvatarOverrides(current => ({ ...current, [guestId]: dataUrl }));
    onUpdateGuest?.(guestId, { avatar_url: dataUrl });
  };
  
  const handleAvatarRemove = (guestId) => {
    setAvatarOverrides(current => ({ ...current, [guestId]: null }));
    onUpdateGuest?.(guestId, { avatar_url: null });
  };
  
  const resolveAvatar = (guest) => {
    if (Object.prototype.hasOwnProperty.call(avatarOverrides, guest.id)) {
      return avatarOverrides[guest.id];
    }
    return guest.avatar_url || null;
  };
  
  return (
    <div data-testid="guest-list-tab" style={{ padding: 0 }}>
      {/* Modals */}
      <CsvImportModal
        visible={showImport}
        onClose={() => setShowImport(false)}
        onImport={handleImport}
      />
      <AvatarMenuModal
        visible={!!avatarMenuGuest}
        guest={avatarMenuGuest}
        onClose={() => setAvatarMenuGuest(null)}
        onUpload={handleAvatarUpload}
        onRemove={handleAvatarRemove}
      />
      
      {/* Panel Header */}
      <AdminPageHeader
        eyebrow="Guest List"
        title="Guests"
        tagline="Guest roster, invitation status, and attendance journey."
        right={(
          <button
            style={styles.addGuestBtn}
            onClick={() => setShowAddForm(!showAddForm)}
            data-testid="add-guest-btn"
          >
            <div style={styles.addGuestBtnIcon}>
              {showAddForm ? <X size={14} color="#fff" /> : <UserPlus size={14} color="#fff" />}
            </div>
            {showAddForm ? 'Cancel' : 'Add Guest'}
          </button>
        )}
      />

      {/* KPI integrity warning — renders only when integrity fails (decision-critical surface). */}
      <KPIIntegrityIndicator integrityCheck={integrityCheck} variant="inline-warning" context="guests" />
      
      {/* Metrics Strip */}
      <div style={styles.metricsStrip}>
        {metrics.map((metric, idx) => {
          const isActive = activeMetric === metric.filterKey;
          const isClickable = !!metric.filterKey;
          return (
            <div
              key={metric.label}
              data-testid={`metric-${metric.label.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={isClickable ? () => setActiveMetric(isActive ? null : metric.filterKey) : undefined}
              style={{
                ...styles.metricCell,
                ...(idx === metrics.length - 1 ? styles.metricCellLast : {}),
                cursor: isClickable ? 'pointer' : 'default',
                background: isActive ? 'rgba(198,167,90,0.12)' : 'transparent',
                borderBottom: isActive ? '2px solid #C6A75A' : '2px solid transparent',
                transition: 'background 0.15s, border-color 0.15s',
              }}
            >
              <div style={{ ...styles.metricLabel, color: isActive ? '#7B6125' : COLORS.textLight }}>{metric.label}</div>
              <div style={{ ...styles.metricValue, color: isActive ? '#7B6125' : '#181713' }}>{metric.value}</div>
            </div>
          );
        })}
        {activeMetric && (
          <div
            onClick={() => setActiveMetric(null)}
            style={{ position: 'absolute', right: 12, top: 12, cursor: 'pointer', fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: 4 }}
          >
            <X size={12} /> Clear Filter
          </div>
        )}
      </div>
      
      {/* Guest Ops Row — Review Workspace + Conflicts Panel */}
      <div style={styles.opsRow}>
        {/* ═══ LEFT CARD: Unrestricted Guest Review Workspace ═══ */}
        <div style={{ ...styles.opsCard, ...styles.opsCardPrimary, minHeight: 200 }}>
          {reviewQueue.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.3 }}>&#10003;</div>
              <div style={styles.opsTitle}>No Parties Awaiting Review</div>
              <p style={styles.opsBody}>All additional guest submissions have been reviewed.</p>
            </div>
          ) : (() => {
            const party = reviewQueue[reviewIndex] || {};
            const pendingPgs = reviewGuests.filter(pg => pg.status === 'pending');
            const allDispositioned = reviewGuests.length > 0 && pendingPgs.length === 0;
            const statusLabel = party.review_status === 'pending_review' ? 'Pending Review'
              : party.review_status === 'duplicate_review' ? 'Duplicate Review'
              : party.review_status === 'in_review' ? 'In Review'
              : party.review_status === 'ready_to_send' ? 'Ready to Send'
              : 'Confirmed';
            const statusColor = party.review_status === 'ready_to_send' ? '#2F7A57'
              : party.review_status === 'duplicate_review' ? '#D4A017'
              : party.review_status === 'pending_review' ? '#E53E3E' : '#6F6658';
            return (
              <>
                {/* Navigation header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div style={styles.opsEyebrow}>Additional Guest Review</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 10, color: '#E53E3E', fontWeight: 700, background: 'rgba(229,62,62,0.1)', padding: '2px 8px', borderRadius: 4 }}>
                      {reviewQueue.length} {reviewQueue.length === 1 ? 'party' : 'parties'}
                    </span>
                    <button data-testid="review-prev" onClick={() => setReviewIndex(i => Math.max(0, i - 1))} disabled={reviewIndex === 0}
                      style={{ width: 24, height: 24, borderRadius: 6, border: `1px solid ${COLORS.border}`, background: reviewIndex === 0 ? '#f5f3f0' : COLORS.cream, cursor: reviewIndex === 0 ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#1A1A1A', opacity: reviewIndex === 0 ? 0.3 : 1 }}>
                      &#8249;
                    </button>
                    <span style={{ fontSize: 10, color: COLORS.textMuted, fontWeight: 600 }}>{reviewIndex + 1} of {reviewQueue.length}</span>
                    <button data-testid="review-next" onClick={() => setReviewIndex(i => Math.min(reviewQueue.length - 1, i + 1))} disabled={reviewIndex === reviewQueue.length - 1}
                      style={{ width: 24, height: 24, borderRadius: 6, border: `1px solid ${COLORS.border}`, background: reviewIndex === reviewQueue.length - 1 ? '#f5f3f0' : COLORS.cream, cursor: reviewIndex === reviewQueue.length - 1 ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#1A1A1A', opacity: reviewIndex === reviewQueue.length - 1 ? 0.3 : 1 }}>
                      &#8250;
                    </button>
                  </div>
                </div>
                {/* Party header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <div style={styles.opsTitle}>{party.party_name || 'Unknown Party'}</div>
                  <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: `${statusColor}15`, color: statusColor, letterSpacing: '0.5px', textTransform: 'uppercase' }}>{statusLabel}</span>
                </div>
                {/* RSVP History — compact, read-only, only when submitted */}
                {party.rsvp_submitted_at && (
                  <div data-testid="rsvp-history-strip" style={{
                    padding: '6px 10px', borderRadius: 8, marginBottom: 8,
                    background: 'rgba(107,114,128,0.06)',
                    border: '1px solid rgba(107,114,128,0.18)',
                  }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted, letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 3 }}>
                      RSVP History{(party.rsvp_revision && party.rsvp_revision > 1) ? ` · rev ${party.rsvp_revision}` : ''}
                    </div>
                    <div style={{ fontSize: 11, color: '#1F1C17', lineHeight: 1.4 }}>
                      Submitted by <span style={{ fontWeight: 600 }}>{party.rsvp_submitted_by_name || 'Unknown'}</span>
                      {party.rsvp_submitted_at ? ` · ${new Date(party.rsvp_submitted_at).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}
                    </div>
                    {party.rsvp_last_updated_at && (party.rsvp_last_updated_at !== party.rsvp_submitted_at || party.rsvp_last_updated_by_name !== party.rsvp_submitted_by_name) && (
                      <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.4 }}>
                        Last updated by <span style={{ fontWeight: 600, color: '#1F1C17' }}>{party.rsvp_last_updated_by_name || 'Unknown'}</span>
                        {party.rsvp_last_updated_at ? ` · ${new Date(party.rsvp_last_updated_at).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}
                      </div>
                    )}
                  </div>
                )}
                <p style={{ ...styles.opsBody, marginTop: 2, marginBottom: 12 }}>{party.total_provisional || reviewGuests.length} additional guest{(party.total_provisional || reviewGuests.length) !== 1 ? 's' : ''} submitted via RSVP</p>
                {/* Provisional guests list */}
                {reviewLoading ? (
                  <div style={{ textAlign: 'center', padding: 16, color: COLORS.textMuted, fontSize: 12 }}>Loading...</div>
                ) : (
                  <div style={{ flex: '1 1 auto', minHeight: 80, overflowY: 'auto' }}>
                    {reviewGuests.map(pg => (
                      <div key={pg.id} data-testid={`review-pg-${pg.id}`} style={{
                        padding: '10px 12px', marginBottom: 6, borderRadius: 10,
                        border: `1px solid ${pg.status === 'pending' ? (pg.duplicate_flag ? 'rgba(212,160,23,0.4)' : COLORS.border) : 'rgba(47,122,87,0.3)'}`,
                        background: pg.status === 'pending' ? (pg.duplicate_flag ? 'rgba(212,160,23,0.04)' : COLORS.cream) : 'rgba(47,122,87,0.04)',
                      }}>
                        {editingPg === pg.id ? (
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                            <input value={editPgData.first_name || ''} onChange={e => setEditPgData(p => ({ ...p, first_name: e.target.value }))}
                              placeholder="First" style={{ flex: 1, minWidth: 80, height: 30, padding: '0 8px', borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 12, outline: 'none' }} />
                            <input value={editPgData.last_name || ''} onChange={e => setEditPgData(p => ({ ...p, last_name: e.target.value }))}
                              placeholder="Last" style={{ flex: 1, minWidth: 80, height: 30, padding: '0 8px', borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 12, outline: 'none' }} />
                            <select value={editPgData.age_group || 'ADULT'} onChange={e => setEditPgData(p => ({ ...p, age_group: e.target.value }))}
                              style={{ height: 30, padding: '0 6px', borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 11 }}>
                              <option value="ADULT">Adult</option>
                              <option value="CHILD">Child</option>
                            </select>
                            <button onClick={() => handlePgAction(pg.id, 'edit', editPgData)} style={{ height: 30, padding: '0 10px', borderRadius: 6, border: `1px solid ${COLORS.gold}`, background: `${COLORS.gold}10`, color: COLORS.goldDark, fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>Save</button>
                            <button onClick={() => setEditingPg(null)} style={{ height: 30, padding: '0 8px', borderRadius: 6, border: `1px solid ${COLORS.border}`, background: 'transparent', color: COLORS.textMuted, fontSize: 10, cursor: 'pointer' }}>Cancel</button>
                          </div>
                        ) : (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 14, fontWeight: 600, color: '#1F1C17' }}>{pg.first_name} {pg.last_name}</span>
                                <span style={{ fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 4, background: pg.age_group === 'CHILD' ? 'rgba(59,130,246,0.1)' : 'rgba(107,114,128,0.1)', color: pg.age_group === 'CHILD' ? '#3B82F6' : '#6B7280' }}>{pg.age_group}</span>
                                <span style={{ fontSize: 8, fontWeight: 600, padding: '1px 5px', borderRadius: 3, background: 'rgba(198,167,90,0.12)', color: COLORS.goldDark, letterSpacing: '0.3px' }}>VIA RSVP</span>
                              </div>
                              {pg.status !== 'pending' && (
                                <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 4, textTransform: 'uppercase',
                                  background: pg.status === 'approved' ? 'rgba(47,122,87,0.1)' : pg.status === 'merged' ? 'rgba(59,130,246,0.1)' : 'rgba(229,62,62,0.1)',
                                  color: pg.status === 'approved' ? '#2F7A57' : pg.status === 'merged' ? '#3B82F6' : '#E53E3E' }}>{pg.status}</span>
                              )}
                            </div>
                            {pg.duplicate_flag && pg.status === 'pending' && (
                              <div style={{ padding: '6px 8px', borderRadius: 6, marginBottom: 6, background: 'rgba(212,160,23,0.08)', border: '1px solid rgba(212,160,23,0.25)' }}>
                                <span style={{ fontSize: 10, fontWeight: 700, color: '#D4A017' }}>Possible Duplicate: </span>
                                {(pg.duplicate_matches || []).slice(0, 2).map((dm, i) => (
                                  <span key={i} style={{ fontSize: 10, color: '#6F6658' }}>{dm.name} ({dm.match_score.toFixed(0)}%){i < 1 && pg.duplicate_matches.length > 1 ? ', ' : ''}</span>
                                ))}
                              </div>
                            )}
                            {pg.status === 'pending' && (
                              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                                <button data-testid={`pg-approve-${pg.id}`} onClick={() => handlePgAction(pg.id, 'approve')}
                                  style={{ fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(47,122,87,0.3)', background: 'rgba(47,122,87,0.06)', color: '#2F7A57', cursor: 'pointer' }}>Approve</button>
                                <button data-testid={`pg-delete-${pg.id}`} onClick={() => handlePgAction(pg.id, 'delete')}
                                  style={{ fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(229,62,62,0.3)', background: 'rgba(229,62,62,0.06)', color: '#E53E3E', cursor: 'pointer' }}>Delete</button>
                                <button onClick={() => { setEditingPg(pg.id); setEditPgData({ first_name: pg.first_name, last_name: pg.last_name, age_group: pg.age_group }); }}
                                  style={{ fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 6, border: `1px solid ${COLORS.border}`, background: 'transparent', color: COLORS.textMuted, cursor: 'pointer' }}>Edit</button>
                                {pg.duplicate_flag && (
                                  <button onClick={() => handlePgAction(pg.id, 'dismiss-duplicate')}
                                    style={{ fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(212,160,23,0.3)', background: 'rgba(212,160,23,0.06)', color: '#D4A017', cursor: 'pointer' }}>Keep Separate</button>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {/* Confirmation — per-person Send / Don't Send (Jul 2026) */}
                {allDispositioned && party.confirmation_status !== 'sent' && (() => {
                  const members = (guests || []).filter(g =>
                    g.party_id === party.party_id && !g.is_deleted &&
                    String(g.rsvp_status || '').toLowerCase() === 'yes');
                  const sendIds = members.filter(m => !confirmSkip[m.id]).map(m => m.id);
                  const busy = sendingConfirmationFor === party.party_id;
                  return (
                    <div data-testid="confirmation-recipients" style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted, letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 6 }}>
                        Confirmation email — choose who receives it
                      </div>
                      {members.length === 0 ? (
                        <div style={{ fontSize: 11, color: COLORS.textMuted, fontStyle: 'italic', marginBottom: 6 }}>
                          No attending members with this party.
                        </div>
                      ) : members.map(m => {
                        const skipped = !!confirmSkip[m.id];
                        const hasEmail = !!String(m.email || '').trim();
                        return (
                          <div key={m.id} data-testid={`confirm-recipient-${m.id}`}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '5px 8px', marginBottom: 4, borderRadius: 8, border: `1px solid ${skipped ? COLORS.border : 'rgba(47,122,87,0.3)'}`, background: skipped ? 'rgba(107,114,128,0.05)' : 'rgba(47,122,87,0.05)' }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: skipped ? COLORS.textMuted : '#1F1C17', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {(m.first_name || '') + ' ' + (m.last_name || '')}
                              {!hasEmail && <span style={{ fontSize: 9, color: '#D4A017', marginLeft: 6, fontStyle: 'italic' }}>no email — uses respondent's</span>}
                            </span>
                            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                              <button data-testid={`confirm-send-${m.id}`}
                                onClick={() => setConfirmSkip(p => ({ ...p, [m.id]: false }))}
                                style={{ fontSize: 9, fontWeight: 700, padding: '3px 8px', borderRadius: 5, cursor: 'pointer', border: `1px solid ${!skipped ? 'rgba(47,122,87,0.5)' : COLORS.border}`, background: !skipped ? 'rgba(47,122,87,0.15)' : 'transparent', color: !skipped ? '#2F7A57' : COLORS.textMuted }}>
                                Send Confirmation
                              </button>
                              <button data-testid={`confirm-skip-${m.id}`}
                                onClick={() => setConfirmSkip(p => ({ ...p, [m.id]: true }))}
                                style={{ fontSize: 9, fontWeight: 700, padding: '3px 8px', borderRadius: 5, cursor: 'pointer', border: `1px solid ${skipped ? 'rgba(229,62,62,0.5)' : COLORS.border}`, background: skipped ? 'rgba(229,62,62,0.12)' : 'transparent', color: skipped ? '#E53E3E' : COLORS.textMuted }}>
                                Don't Send
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      <button data-testid="send-confirmation-btn"
                        onClick={() => handleSendConfirmation(party.party_id, sendIds)}
                        disabled={busy}
                        style={{ marginTop: 8, width: '100%', padding: '10px 0', borderRadius: 8, border: '1px solid rgba(47,122,87,0.4)', background: busy ? 'linear-gradient(180deg, #6F6658 0%, #4A4439 100%)' : (sendIds.length > 0 ? 'linear-gradient(180deg, #2F7A57 0%, #1F5A3D 100%)' : 'linear-gradient(180deg, #6F6658 0%, #4A4439 100%)'), color: '#fff', fontSize: 13, fontWeight: 700, cursor: busy ? 'default' : 'pointer', letterSpacing: '0.5px', opacity: busy ? 0.7 : 1 }}>
                        {busy ? 'Sending…' : (sendIds.length > 0
                          ? `Send Confirmation (${sendIds.length} of ${members.length})`
                          : 'Close Review Without Sending')}
                      </button>
                    </div>
                  );
                })()}
                {party.confirmation_status === 'sent' && (
                  <div style={{ marginTop: 12, padding: '8px 12px', borderRadius: 8, background: 'rgba(47,122,87,0.08)', border: '1px solid rgba(47,122,87,0.25)', textAlign: 'center' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#2F7A57' }}>Confirmation Sent</span>
                  </div>
                )}
              </>
            );
          })()}
        </div>
        
        {/* ═══ RIGHT CARD: Conflicts & Duplicates Panel ═══ */}
        <div style={styles.opsCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={styles.opsEyebrow}>Conflicts &amp; Duplicates</div>
            {globalDuplicates.length > 0 && (
              <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: 'rgba(212,160,23,0.12)', color: '#D4A017' }}>
                {globalDuplicates.length} unresolved
              </span>
            )}
          </div>
          {globalDuplicates.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.3 }}>&#10003;</div>
              <div style={styles.opsTitle}>No Conflicts</div>
              <p style={styles.opsBody}>No duplicate or near-duplicate issues detected.</p>
            </div>
          ) : (
            <div style={{ flex: '1 1 auto', minHeight: 80, overflowY: 'auto' }}>
              {globalDuplicates.map(pg => (
                <div key={pg.id} style={{ padding: '10px 12px', marginBottom: 6, borderRadius: 10, border: '1px solid rgba(212,160,23,0.25)', background: 'rgba(212,160,23,0.04)' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1F1C17', marginBottom: 2 }}>
                    {pg.first_name} {pg.last_name}
                    <span style={{ fontSize: 9, fontWeight: 600, marginLeft: 6, padding: '1px 5px', borderRadius: 3, background: 'rgba(198,167,90,0.12)', color: COLORS.goldDark }}>NEW</span>
                  </div>
                  {(pg.duplicate_matches || []).slice(0, 2).map((dm, i) => (
                    <div key={i} style={{ fontSize: 11, color: '#6F6658', marginBottom: 2 }}>
                      Matches: <strong>{dm.name}</strong> ({dm.match_score.toFixed(0)}% — {dm.level})
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                    {pg.duplicate_matches && pg.duplicate_matches[0] && pg.duplicate_matches[0].guest_id && (
                      <button
                        title={`Combine this entry into the existing guest "${pg.duplicate_matches[0].name}" — keeps one record and removes the duplicate.`}
                        onClick={() => { if (window.confirm(`Merge "${pg.first_name} ${pg.last_name}" into existing guest "${pg.duplicate_matches[0].name}"?\n\nTheir details are combined into the existing guest and this duplicate entry is removed.`)) handlePgAction(pg.id, 'merge', { mergeWith: pg.duplicate_matches[0].guest_id }); }}
                        style={{ fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(37,99,235,0.35)', background: 'rgba(37,99,235,0.08)', color: '#2563EB', cursor: 'pointer' }}>
                        Merge into existing
                      </button>
                    )}
                    <button
                      title="These are two different people — keep both and stop flagging this as a duplicate."
                      onClick={() => handlePgAction(pg.id, 'dismiss-duplicate')}
                      style={{ fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(212,160,23,0.35)', background: 'rgba(212,160,23,0.08)', color: '#D4A017', cursor: 'pointer' }}>
                      Keep separate
                    </button>
                    <button
                      title="Remove this duplicate submission entirely."
                      onClick={() => { if (window.confirm(`Delete "${pg.first_name} ${pg.last_name}" from review?\n\nThis removes the duplicate submission. The existing guest is not affected.`)) handlePgAction(pg.id, 'delete'); }}
                      style={{ fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(229,62,62,0.35)', background: 'rgba(229,62,62,0.06)', color: '#E53E3E', cursor: 'pointer' }}>
                      Delete
                    </button>
                    <button
                      title="Open this person's party in the review panel on the left to see full details before deciding."
                      onClick={() => { const idx = reviewQueue.findIndex(q => q.party_id === pg.party_id); if (idx >= 0) setReviewIndex(idx); }}
                      style={{ fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 6, border: `1px solid ${COLORS.border}`, background: 'transparent', color: COLORS.textMuted, cursor: 'pointer' }}>
                      Review in party
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Bulk Bar */}
      {selectedCount > 0 && (
        <div style={styles.bulkBar}>
          <span style={styles.bulkBarText}>{selectedCount} selected</span>
          <div style={styles.bulkBarActions}>
            <button style={styles.bulkAction} onClick={() => exportGuests(guests.filter(g => selectedIds.includes(g.id)), 'csv')} data-testid="bulk-export-csv">
              Export CSV
            </button>
            <button style={styles.bulkAction} onClick={() => exportGuests(guests.filter(g => selectedIds.includes(g.id)), 'xlsx')} data-testid="bulk-export-xlsx">
              Export Excel
            </button>
            {selectedCount === 2 && (
              <button style={{ ...styles.bulkAction, background: 'rgba(63,199,204,0.15)', color: '#3FC7CC', border: '1px solid rgba(63,199,204,0.35)' }} onClick={() => {
                // ISSUE #1 (Feb 2026): open Field-Level Merge Modal.
                // Default each field to "a" (the first selected guest = survivor).
                const a = guests.find(g => g.id === selectedIds[0]);
                const b = guests.find(g => g.id === selectedIds[1]);
                if (!a || !b) { toast.error('Selected guests not found'); return; }
                const fields = ['first_name', 'last_name', 'email', 'phone',
                  'age_group', 'rsvp_status', 'party_id', 'other_notes',
                  'allergies', 'dietary_requirements', 'source_detail'];
                const seed = {};
                fields.forEach(f => {
                  const av = a[f]; const bv = b[f];
                  // Smart default: if A is empty/null and B has a value, default to B.
                  // Otherwise default to A (survivor wins by default).
                  const aEmpty = av === null || av === undefined || av === '';
                  const bHasVal = bv !== null && bv !== undefined && bv !== '';
                  seed[f] = (aEmpty && bHasVal) ? 'b' : 'a';
                });
                setMergeFieldSelections(seed);
                setMergeModalOpen(true);
              }} data-testid="bulk-merge-btn">
                Merge (2)
              </button>
            )}
            {selectedCount >= 2 && (
              <button style={{ ...styles.bulkAction, background: 'rgba(198,167,90,0.15)', color: '#C6A75A', border: '1px solid rgba(198,167,90,0.35)' }}
                onClick={() => {
                  setGroupName('');
                  setGroupPrimaryId('');
                  setGroupError('');
                  setShowGroupModal(true);
                }}
                data-testid="bulk-group-btn">
                Group ({selectedCount})
              </button>
            )}
            {/* Mode toggle — only for guests WITH party_id */}
            {(() => {
              const withParty = guests.filter(g => selectedIds.includes(g.id) && g.party_id);
              const partyIds = [...new Set(withParty.map(g => g.party_id))];
              if (partyIds.length === 0) return null;
              return (
                <button style={{ ...styles.bulkAction, background: 'rgba(212,160,23,0.12)', color: '#9B7420', border: '1px solid rgba(212,160,23,0.30)' }}
                  data-testid="bulk-set-restricted-btn"
                  onClick={async () => {
                    if (!window.confirm(`Set ${partyIds.length} ${partyIds.length === 1 ? 'party' : 'parties'} to Restricted mode? This prevents named additional-guest entry during RSVP.`)) return;
                    try {
                      await fetch(`${API}/api/parties/bulk-set-mode`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ party_ids: partyIds, mode: 'restricted' }),
                      });
                      toast.success(`${partyIds.length} ${partyIds.length === 1 ? 'party' : 'parties'} set to Restricted`);
                      setSelectedIds([]);
                      refresh?.();
                    } catch { toast.error('Failed to set mode'); }
                  }}>
                  Set Restricted
                </button>
              );
            })()}
            <button style={{ ...styles.bulkAction, background: 'rgba(229,62,62,0.15)', color: '#E53E3E', border: '1px solid rgba(229,62,62,0.35)' }} onClick={async () => {
              if (!window.confirm(`Delete ${selectedCount} guest${selectedCount > 1 ? 's' : ''}? This cannot be undone.`)) return;
              for (const gid of selectedIds) { await onDeleteGuest(gid); }
              setSelectedIds([]);
            }} data-testid="bulk-delete-btn">
              Delete ({selectedCount})
            </button>
            <button style={styles.bulkActionGhost} onClick={() => setSelectedIds([])}>
              Clear
            </button>
          </div>
        </div>
      )}
      
      {/* ═══ Item 5 — Milo Add Guest prefill banner ═══════════════════════
          Renders only after Milo's L1 add-guest prefill has been
          consumed. Sits directly above the Add Guest form so the
          operator sees it the moment the form expands. Clearly states:
          Milo prepared the form — Milo did NOT save, submit, or create
          any guest. */}
      {miloPrefillBanner && (
        <div
          data-testid="milo-add-guest-prefill-banner"
          style={{
            margin: '0 0 12px',
            padding: '12px 14px',
            border: `1px solid ${miloPrefillBanner.ok ? 'rgba(198,167,90,0.45)' : 'rgba(180,60,60,0.4)'}`,
            background: miloPrefillBanner.ok ? 'rgba(198,167,90,0.08)' : 'rgba(180,60,60,0.06)',
            borderRadius: 10,
            color: COLORS.text || '#1B1812',
            fontSize: 13,
            lineHeight: 1.55,
            display: 'flex', gap: 12, alignItems: 'flex-start',
            justifyContent: 'space-between',
          }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              {miloPrefillBanner.title}
            </div>
            {miloPrefillBanner.summary && (
              <div style={{ marginBottom: 4 }}>
                <span style={{ opacity: 0.7 }}>Proposed action: </span>
                <span data-testid="milo-add-guest-prefill-summary">{miloPrefillBanner.summary}</span>
              </div>
            )}
            {miloPrefillBanner.first_name && (
              <div style={{ marginBottom: 4 }}>
                <span style={{ opacity: 0.7 }}>Seeded values: </span>
                <strong data-testid="milo-add-guest-prefill-name">
                  {miloPrefillBanner.first_name} {miloPrefillBanner.last_name}
                </strong>
                {miloPrefillBanner.email && (
                  <span style={{ opacity: 0.7 }}> · email <code>{miloPrefillBanner.email}</code></span>
                )}
                {miloPrefillBanner.phone && (
                  <span style={{ opacity: 0.7 }}> · phone <code>{miloPrefillBanner.phone}</code></span>
                )}
              </div>
            )}
            <div style={{ marginTop: 4 }}>{miloPrefillBanner.detail}</div>
            {(miloPrefillBanner.runbook_family_id || miloPrefillBanner.runbook_version) && (
              <div style={{ marginTop: 6, fontSize: 11, opacity: 0.75 }}>
                Grounded in runbook: {miloPrefillBanner.runbook_family_id} v{miloPrefillBanner.runbook_version}
              </div>
            )}
          </div>
          <button
            data-testid="milo-add-guest-prefill-banner-dismiss"
            onClick={() => setMiloPrefillBanner(null)}
            style={{
              flexShrink: 0,
              background: 'transparent',
              border: '1px solid rgba(0,0,0,0.18)',
              borderRadius: 6,
              padding: '4px 10px',
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
              color: 'inherit',
            }}>
            Dismiss banner
          </button>
        </div>
      )}

      {/* Add Guest Form */}
      {showAddForm && (
        <div style={styles.addGuestCard}>
          <div style={styles.cardEyebrow}>Add Guest</div>
          <div style={styles.formRow}>
            <input
              style={styles.formInput}
              placeholder="First Name *"
              value={newGuest.first_name}
              onChange={e => setNewGuest({ ...newGuest, first_name: e.target.value })}
              data-testid="new-guest-first-name"
            />
            <input
              style={styles.formInput}
              placeholder="Last Name *"
              value={newGuest.last_name}
              onChange={e => setNewGuest({ ...newGuest, last_name: e.target.value })}
              data-testid="new-guest-last-name"
            />
          </div>
          <div style={styles.formRow}>
            <input
              style={styles.formInput}
              placeholder="Email"
              value={newGuest.email}
              onChange={e => setNewGuest({ ...newGuest, email: e.target.value })}
              data-testid="new-guest-email"
            />
            <input
              style={styles.formInput}
              placeholder="Phone"
              value={newGuest.phone}
              onChange={e => setNewGuest({ ...newGuest, phone: formatPhoneInput(e.target.value) })}
              data-testid="new-guest-phone"
            />
          </div>
          <button
            style={styles.addGuestSubmit}
            onClick={handleAdd}
            disabled={isAddingGuest}
            data-testid="save-new-guest-btn"
          >
            {isAddingGuest ? 'Adding...' : 'Add Guest'}
          </button>
        </div>
      )}
      
      {/* Toolbar Card */}
      <div style={styles.toolbarCard}>
        <div style={styles.toolbarRow}>
          <div style={{
            ...styles.searchField,
            ...(isExternalSearch ? styles.searchFieldExternal : {}),
          }}>
            <Search size={16} color={isExternalSearch ? COLORS.gold : COLORS.textLight} />
            <input
              style={styles.searchInput}
              placeholder="Search email or phone"
              value={isExternalSearch ? externalSearch : search}
              onChange={e => setSearch(e.target.value)}
              disabled={isExternalSearch}
              data-testid="guest-search-input"
            />
            {!isExternalSearch && search && (
              <div
                data-testid="search-clear-btn"
                onClick={() => setSearch('')}
                style={{
                  width: 20, height: 20, borderRadius: '50%', flexShrink: 0, cursor: 'pointer',
                  background: 'rgba(0,0,0,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: '1px solid rgba(0,0,0,0.12)',
                }}
              >
                <X size={12} color={COLORS.textMuted} />
              </div>
            )}
            {isExternalSearch && <span style={styles.externalSearchBadge}>External</span>}
          </div>
          <div style={styles.selectWrap}>
            <SelectMenu
              label="RSVP"
              value={filter}
              onSelect={setFilter}
              options={[
                { value: 'all', label: 'All Guests' },
                { value: 'yes', label: 'Attending' },
                { value: 'no', label: 'Declined' },
                { value: 'pending', label: 'Pending' },
                { value: 'missing-rsvp', label: 'Missing RSVP' },
              ]}
            />
            <SelectMenu
              label="Sort"
              value={sortMode}
              onSelect={setSortMode}
              options={[
                { value: 'recent', label: 'Recent' },
                { value: 'first', label: 'First' },
                { value: 'last', label: 'Last' },
                { value: 'group', label: 'Group' },
                { value: 'rsvp', label: 'RSVP' },
              ]}
            />
            {/* Step 2 correction (Feb 2026) — Invite-open status filter.
                Lives next to Sort as a peer dropdown (no beige strip).
                Drives the bottom guest list via guestMatchesInviteFilter. */}
            <SelectMenu
              label="Invite"
              value={inviteFilter || 'all'}
              onSelect={(v) => setInviteFilter(v === 'all' ? null : v)}
              options={[
                { value: 'all',                          label: 'All' },
                { value: 'invite_not_sent',              label: 'Not yet sent' },
                { value: 'invite_sent_not_opened',       label: 'Sent · not opened' },
                { value: 'invite_opened_not_rsvped',     label: 'Opened · awaiting RSVP' },
                { value: 'invite_opened_and_rsvped',     label: 'Opened · RSVPed' },
                { value: 'invite_reached_landing_only',  label: 'Reached landing only' },
              ]}
            />
          </div>
          <div style={styles.controlActionsRow}>
            <button 
              style={styles.utilityBtn} 
              onClick={() => {
                // Navigate to Messaging tab's Guest Journey subtab
                if (onNavigateToTab) {
                  onNavigateToTab('messaging', { subtab: 'journey' });
                }
              }}
              data-testid="configure-journey-btn"
            >
              <ExternalLink size={12} color={COLORS.goldDark} />
              Configure Journey
            </button>
            <button
              style={styles.utilityBtn}
              onClick={handleAssignMissingTokens}
              disabled={assigningTokens}
              data-testid="assign-missing-tokens-btn"
            >
              <Key size={12} color={COLORS.goldDark} />
              {assigningTokens ? 'Assigning…' : 'Assign Missing URL Tokens'}
            </button>
            <button style={styles.utilityBtn} onClick={() => setShowImport(true)}>
              <Upload size={12} color={COLORS.goldDark} />
              Import CSV
            </button>
            <div style={{ position: 'relative' }} ref={exportMenuRef}>
              <button
                style={styles.utilityBtn}
                onClick={() => setExportMenuOpen(v => !v)}
                data-testid="export-menu-btn"
              >
                <Download size={12} color={COLORS.goldDark} />
                Export ▾
              </button>
              {exportMenuOpen && (
                <div data-testid="export-menu" style={{
                  position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 20,
                  background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: 8,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: 4, minWidth: 200,
                }}>
                  <button
                    style={{ ...styles.utilityBtn, width: '100%', justifyContent: 'flex-start', border: 'none' }}
                    onClick={() => { setExportMenuOpen(false); exportGuests(guests, 'csv'); }}
                    data-testid="export-csv-option"
                  >
                    CSV (Google Sheets)
                  </button>
                  <button
                    style={{ ...styles.utilityBtn, width: '100%', justifyContent: 'flex-start', border: 'none' }}
                    onClick={() => { setExportMenuOpen(false); exportGuests(guests, 'xlsx'); }}
                    data-testid="export-xlsx-option"
                  >
                    Excel (.xlsx)
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Step 2 correction (Feb 2026): the previous beige filter strip has been
          removed. The invite-open filter now lives as a peer dropdown next to
          Sort in the toolbar above. The dropdown both reflects and drives the
          inviteFilter state (including any value handed off from the dashboard
          funnel via sessionStorage). */}

      {/* Table or Empty State */}
      {filteredGuests.length === 0 ? (
        <div style={styles.emptyState}>
          <Users size={44} color={COLORS.grey} />
          <div style={styles.emptyText}>
            {guests.length === 0 ? 'No guests yet' : 'No matches found'}
          </div>
          <div style={styles.emptySubtext}>
            {guests.length === 0
              ? 'Add a guest or import a CSV to build your roster.'
              : 'Try adjusting your search or filter settings.'}
          </div>
        </div>
      ) : (
        <div style={styles.tableCard}>
          <div style={{ overflowX: 'auto' }}>
            <div style={styles.tableFrame}>
              {/* Table Header */}
              <div style={styles.tableHeaderRow}>
                {/* Line-number spacer — aligns header columns with body rows
                    (body rows render a 28px line number cell before the
                    checkbox; the header must mirror that spacer for the
                    column titles to sit directly above their data). */}
                <div aria-hidden="true" style={{ width: 28, flexShrink: 0 }} />
                <div
                  style={styles.checkboxCell}
                  onClick={() => setSelectedIds(allVisibleSelected ? [] : filteredGuests.map(g => g.id))}
                >
                  <div style={{
                    ...styles.checkbox,
                    ...(allVisibleSelected ? styles.checkboxChecked : {}),
                  }}>
                    {allVisibleSelected && <Check size={10} color="#fff" />}
                  </div>
                </div>
                <div style={{ ...styles.headerCell, ...styles.nameCol }}>Name & Contact</div>
                <div style={{ ...styles.headerCell, ...styles.groupCol }}>Grouping</div>
                <div style={{ ...styles.headerCell, ...styles.rsvpCol }}>RSVP Status</div>
                <div style={{ ...styles.headerCell, ...styles.partyCol }}>Party</div>
                <div style={{ ...styles.headerCell, ...styles.journeyCol, textAlign: 'center' }}>Guest Journey</div>
                <div style={{ ...styles.headerCell, ...styles.alertCol }}>Alerts</div>
                <div style={{ ...styles.headerCell, ...styles.actionCol }}>Actions</div>
              </div>
              
              {/* Table Body */}
              <div style={styles.tableBody}>
                {filteredGuests.map((guest, guestIndex) => {
                  const isSelected = selectedIds.includes(guest.id);
                  const isHovered = hoveredRowId === guest.id;
                  const rsvpLabel = rsvpIs(guest, 'yes', 'confirmed')
                    ? 'Attending'
                    : rsvpIs(guest, 'no', 'declined')
                    ? 'Declined'
                    : guest.invitation_status === 'failed'
                    ? 'Delivery Issue'
                    : 'Pending';
                  const rsvpStyle = rsvpIs(guest, 'yes', 'confirmed')
                    ? styles.rsvpSuccess
                    : rsvpIs(guest, 'no', 'declined')
                    ? styles.rsvpDanger
                    : guest.invitation_status === 'failed'
                    ? styles.rsvpDanger
                    : styles.rsvpWarning;
                  const rsvpTextColor = rsvpIs(guest, 'yes', 'confirmed')
                    ? '#2F7A57'
                    : rsvpIs(guest, 'no', 'declined') || guest.invitation_status === 'failed'
                    ? '#B24E4E'
                    : '#9B7420';
                  // UNIFICATION (Feb 2026): Show this guest's own party composition
                  // derived from age_group (Path B). Per-guest scalars deprecated.
                  // Falls back to deriving from age_group when backend hasn't
                  // populated party_adults_count (older payload or orphan guest).
                  const isAdult = (guest.age_group || '').toUpperCase() === 'ADULT';
                  const isChild = (guest.age_group || '').toUpperCase() === 'CHILD';
                  const adults = guest.party_adults_count ?? (isAdult ? 1 : 0);
                  const children = guest.party_children_count ?? (isChild ? 1 : 0);
                  const partyText = `${adults} adult${adults === 1 ? '' : 's'}${children > 0 ? ` · ${children} child${children === 1 ? '' : 'ren'}` : ''}`;
                  const avatarUrl = resolveAvatar(guest);
                  
                  return (
                    <div
                      key={guest.id}
                      data-testid={`guest-row-${guest.id}`}
                      style={{
                        ...styles.tableRow,
                        ...(isSelected ? styles.tableRowSelected : {}),
                        ...(isHovered && !isSelected ? styles.tableRowHover : {}),
                      }}
                      onMouseEnter={() => setHoveredRowId(guest.id)}
                      onMouseLeave={() => setHoveredRowId(null)}
                    >
                      {/* Line number */}
                      <div style={{ width: 28, flexShrink: 0, textAlign: 'center', fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.35)', fontFamily: "'DM Sans', system-ui, sans-serif" }}>
                        {guestIndex + 1}
                      </div>
                      {/* Checkbox */}
                      <div style={styles.checkboxCell} onClick={() => toggleGuest(guest.id)}>
                        <div style={{
                          ...styles.checkbox,
                          ...(isSelected ? styles.checkboxChecked : {}),
                        }}>
                          {isSelected && <Check size={10} color="#fff" />}
                        </div>
                      </div>
                      
                      {/* Name & Contact */}
                      <div style={{ ...styles.bodyCell, ...styles.nameCol }}>
                        <div style={styles.identityRow}>
                          <div
                            style={styles.avatarButton}
                            onClick={(e) => { e.stopPropagation(); setAvatarMenuGuest(guest); }}
                          >
                            {avatarUploading[guest.id] && (
                              <div style={styles.avatarSpinner}>
                                <Loader2 size={16} color={COLORS.goldDark} className="animate-spin" />
                              </div>
                            )}
                            {avatarUrl ? (
                              <img src={avatarUrl} alt="" style={styles.avatarImage} />
                            ) : (
                              <span style={styles.initialsText}>{getGuestInitials(guest)}</span>
                            )}
                          </div>
                          <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => setDetailGuest(guest)}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <div style={styles.guestName}>{guest.first_name} {guest.last_name}</div>
                              {guest.is_primary && (
                                <span
                                  data-testid={`primary-star-${guest.id}`}
                                  title="Primary — this party's designated contact (one per party)"
                                  style={{ color: COLORS.goldDark, fontSize: 13, lineHeight: 1, flexShrink: 0 }}
                                >★</span>
                              )}
                              {guest.source === 'rsvp_additional_guest' && (
                                <span data-testid={`rsvp-plus-tag-${guest.id}`} style={{
                                  fontSize: 8, fontWeight: 800, padding: '1px 5px', borderRadius: 3, flexShrink: 0,
                                  background: 'rgba(198,167,90,0.12)', border: '1px solid rgba(198,167,90,0.25)',
                                  color: COLORS.goldDark, letterSpacing: '0.4px',
                                }}>RSVP +1</span>
                              )}
                              {/* Step 2 — invite-open status pill (party/invite-level only).
                                  Wording: "Invite opened" / "Not opened". Tooltip surfaces
                                  first/last/count without claiming a specific guest. */}
                              {(() => {
                                const inv = getInviteOpenStatus(partiesById[guest.party_id]);
                                const fmt = (iso) => {
                                  if (!iso) return '—';
                                  try {
                                    const d = new Date(iso);
                                    return d.toLocaleString(undefined, {
                                      year: 'numeric', month: 'short', day: 'numeric',
                                      hour: '2-digit', minute: '2-digit',
                                    });
                                  } catch { return iso; }
                                };
                                const bg = inv.opened ? 'rgba(212,175,55,0.18)' : 'rgba(120,120,120,0.18)';
                                const border = inv.opened ? 'rgba(212,175,55,0.55)' : 'rgba(120,120,120,0.45)';
                                const color = inv.opened ? '#A57C2C' : '#6B7280';
                                const tip = [
                                  inv.opened ? 'Invite opened' : 'Invite not opened',
                                  `First opened: ${fmt(inv.first_opened_at)}`,
                                  `Last opened:  ${fmt(inv.last_opened_at)}`,
                                  `Open count:   ${inv.open_count}`,
                                  inv.last_open_route ? `Last open route: ${inv.last_open_route}` : null,
                                  inv.deepest_route_reached ? `Deepest route reached: ${inv.deepest_route_reached}` : null,
                                  '— invite-level signal, not certainty about a specific guest —',
                                ].filter(Boolean).join('\n');
                                return (
                                  <span
                                    data-testid={`invite-status-${guest.id}`}
                                    title={tip}
                                    style={{
                                      fontSize: 8, fontWeight: 800, padding: '1px 5px',
                                      borderRadius: 3, flexShrink: 0,
                                      background: bg, border: `1px solid ${border}`,
                                      color, letterSpacing: '0.4px',
                                    }}>
                                    {inv.opened ? `OPENED${inv.open_count > 1 ? ` ×${inv.open_count}` : ''}` : 'NOT OPENED'}
                                  </span>
                                );
                              })()}
                              {(() => {
                                // Celebration guest-hub link opens (distinct from the invite badge).
                                const co = getInviteOpenStatus(partiesById[guest.party_id]);
                                if (!co.celebration_open_count) return null;
                                const fmt = (iso) => {
                                  if (!iso) return '—';
                                  try { return new Date(iso).toLocaleString(); } catch { return iso; }
                                };
                                const tip = [
                                  'Celebration hub link opened',
                                  `First opened: ${fmt(co.celebration_first_opened_at)}`,
                                  `Last opened:  ${fmt(co.celebration_last_opened_at)}`,
                                  `Open count:   ${co.celebration_open_count}`,
                                  '— link-level signal, not certainty about a specific guest —',
                                ].join('\n');
                                return (
                                  <span
                                    data-testid={`celebration-status-${guest.id}`}
                                    title={tip}
                                    style={{
                                      fontSize: 8, fontWeight: 800, padding: '1px 5px',
                                      borderRadius: 3, flexShrink: 0,
                                      background: 'rgba(122,79,160,0.18)', border: '1px solid rgba(122,79,160,0.55)',
                                      color: '#6A4FA0', letterSpacing: '0.4px',
                                    }}>
                                    {`HUB${co.celebration_open_count > 1 ? ` ×${co.celebration_open_count}` : ''}`}
                                  </span>
                                );
                              })()}
                              {(() => {
                                // Thank-you (My Celebration) link opens — the token is per-guest,
                                // so this is guest-level certainty, unlike the party-level badges.
                                if (!guest.my_celebration_open_count) return null;
                                const fmt = (iso) => {
                                  if (!iso) return '—';
                                  try { return new Date(iso).toLocaleString(); } catch { return iso; }
                                };
                                const tip = [
                                  'Thank-you link opened by THIS guest',
                                  `First opened: ${fmt(guest.my_celebration_first_opened_at)}`,
                                  `Last opened:  ${fmt(guest.my_celebration_last_opened_at)}`,
                                  `Open count:   ${guest.my_celebration_open_count}`,
                                ].join('\n');
                                return (
                                  <span
                                    data-testid={`mycelebration-status-${guest.id}`}
                                    title={tip}
                                    style={{
                                      fontSize: 8, fontWeight: 800, padding: '1px 5px',
                                      borderRadius: 3, flexShrink: 0,
                                      background: 'rgba(155,77,107,0.16)', border: '1px solid rgba(155,77,107,0.55)',
                                      color: '#9B4D6B', letterSpacing: '0.4px',
                                    }}>
                                    {`NOTE${guest.my_celebration_open_count > 1 ? ` ×${guest.my_celebration_open_count}` : ''}`}
                                  </span>
                                );
                              })()}
                              {(() => {
                                const mc = guestMessageCounts[guest.id];
                                if (!mc || mc.total === 0) return null;
                                const isUnread = mc.unread > 0;
                                const bg = isUnread ? '#E53E3E' : '#D4A017';
                                const color = '#fff';
                                const count = isUnread ? mc.unread : mc.total;
                                return (
                                  <div data-testid={`msg-badge-${guest.id}`} style={{
                                    width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                                    background: bg, display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', fontSize: 9, fontWeight: 800, color,
                                    boxShadow: isUnread ? '0 0 6px rgba(229,62,62,0.5)' : 'none',
                                  }} title={isUnread ? `${mc.unread} unread message(s)` : `${mc.total} read message(s)`}>
                                    {count}
                                  </div>
                                );
                              })()}
                              {videoGuestbookEnabled && (() => {
                                const gb = gbIndex[guest.id];
                                if (!gb || !gb.has_video) return null;
                                const sent = !!gb.copy_sent_at;
                                let when = '';
                                try { when = gb.copy_sent_at ? ` · ${new Date(gb.copy_sent_at).toLocaleDateString()}` : ''; } catch { when = ''; }
                                const tip = [
                                  'Recorded a video guestbook message',
                                  gb.copy_requested ? 'Requested a copy of their video' : 'Did not request a copy',
                                  sent ? `Copy sent${gb.copy_channel ? ` by ${gb.copy_channel}` : ''}${when}` : (gb.copy_requested ? 'Copy not sent yet' : null),
                                ].filter(Boolean).join('\n');
                                return (
                                  <span data-testid={`gb-badge-${guest.id}`} title={tip} style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 2, flexShrink: 0,
                                    fontSize: 8, fontWeight: 800, padding: '1px 5px', borderRadius: 3, letterSpacing: '0.3px',
                                    background: 'rgba(200,164,85,0.16)', border: '1px solid rgba(200,164,85,0.5)', color: '#8A6A22',
                                  }}>
                                    🎥{gb.copy_requested ? (sent ? ' ✓' : ' 💌') : ''}
                                  </span>
                                );
                              })()}
                              {guest.party_id && plusOneItemsByParty[guest.party_id] && (() => {
                                // ISSUE #6 (Feb 2026): build informative tooltip.
                                // Show display_message + requested names when present.
                                const item = plusOneItemsByParty[guest.party_id];
                                const lines = [item.display_message || 'Pending additional guest request'];
                                if (item.requested_names && item.requested_names.length > 0) {
                                  lines.push('');
                                  lines.push('Requested guests:');
                                  item.requested_names.forEach(n => lines.push(`  • ${n}`));
                                } else if (item.requested_count) {
                                  lines.push(`(${item.requested_count} requested — names not captured in restricted mode)`);
                                }
                                return (
                                  <div data-testid={`plus-one-badge-${guest.id}`} style={{
                                    padding: '1px 6px', borderRadius: 4, flexShrink: 0,
                                    background: 'rgba(229,62,62,0.12)', border: '1px solid rgba(229,62,62,0.30)',
                                    fontSize: 8, fontWeight: 800, color: '#E53E3E', letterSpacing: '0.5px',
                                  }} title={lines.join('\n')}>
                                    +1 REQ
                                  </div>
                                );
                              })()}
                            </div>
                            <div style={styles.guestContact}>
                              {guest.email || <span style={{ color: COLORS.textMuted, fontStyle: 'italic' }}>No email</span>}
                            </div>
                            {guest.phone && (
                              <div style={{ ...styles.guestContact, color: COLORS.textMuted }}>{guest.phone}</div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Grouping */}
                      <div style={{ ...styles.bodyCell, ...styles.groupCol }}>
                        <div style={styles.groupingName}>{formatGroupingLabel(guest)}</div>
                      </div>
                      
                      {/* RSVP Status */}
                      <div style={{ ...styles.bodyCell, ...styles.rsvpCol }}>
                        <div style={{ ...styles.rsvpBadge, ...rsvpStyle }}>
                          <span style={{ ...styles.rsvpBadgeText, color: rsvpTextColor }}>{rsvpLabel}</span>
                        </div>
                        <div style={styles.addedNote}>Added {formatAddedDate(guest.created_at)}</div>
                      </div>
                      
                      {/* Party */}
                      <div style={{ ...styles.bodyCell, ...styles.partyCol }}>
                        <span style={styles.partyText}>{partyText}</span>
                      </div>
                      
                      {/* Guest Journey */}
                      <div style={{ ...styles.bodyCell, ...styles.journeyCol }}>
                        <GuestJourneyCell 
                          guest={guest} 
                          journeySteps={journeySteps}
                          completionsMap={completionsMap}
                          onToggleStep={handleToggleStep}
                          onShowTooltip={showTooltip}
                          onHideTooltip={hideTooltip}
                        />
                      </div>
                      
                      {/* Alerts */}
                      <div style={{ ...styles.bodyCell, ...styles.alertCol }}>
                        <GuestAlertCell 
                          guest={guest}
                          onShowTooltip={showTooltip}
                          onHideTooltip={hideTooltip}
                        />
                      </div>
                      
                      {/* Actions */}
                      <div style={{ ...styles.bodyCell, ...styles.actionCol }}>
                        <button
                          style={styles.actionButton}
                          onClick={() => setDetailGuest(guest)}
                          title="View Details"
                        >
                          <ChevronRight size={16} color="#7B6125" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Tooltip Portal */}
      <TooltipPortal tooltip={tooltip} />
      
      {/* Guest Detail Sheet */}
      {effectiveDetailGuest && (
        <GuestDetailSheet
          guest={effectiveDetailGuest}
          onClose={() => { setDetailGuest(null); onFocusGuestConsumed?.(); refresh?.(); fetchPlusOneItems(); }}
          onDelete={(guestId) => { onDeleteGuest?.(guestId); setDetailGuest(null); onFocusGuestConsumed?.(); refresh?.(); fetchPlusOneItems(); }}
          onUpdateRsvp={onUpdateRsvp}
          onUpdateGuest={onUpdateGuest}
          existingGroups={existingGroups}
          allGuests={guests}
          refresh={refresh}
          onPlusOneResolved={fetchPlusOneItems}
          onMessageDeleted={(guestId, wasUnread) => {
            setGuestMessageCounts(prev => {
              const cur = prev[guestId] || { total: 0, unread: 0 };
              const newTotal = Math.max(0, cur.total - 1);
              const newUnread = wasUnread ? Math.max(0, cur.unread - 1) : cur.unread;
              if (newTotal === 0) {
                const { [guestId]: _, ...rest } = prev;
                return rest;
              }
              return { ...prev, [guestId]: { total: newTotal, unread: newUnread } };
            });
          }}
        />
      )}

      {/* Bulk Group Modal */}
      {showGroupModal && (() => {
        const selectedGuests = guests.filter(g => selectedIds.includes(g.id));
        const existingParties = [...new Map(
          guests.filter(g => g.party_name && g.party_id).map(g => [g.party_id, {
            id: g.party_id,
            name: g.party_name,
            has_primary: guests.some(x => x.party_id === g.party_id && x.is_primary),
          }])
        ).values()];

        // Determine if selected existing group already has a primary
        const selectedExisting = existingParties.find(p => p.name === groupName);
        const existingHasPrimary = selectedExisting?.has_primary || false;
        const needsPrimary = !existingHasPrimary;

        const handleGroupSave = async () => {
          if (!groupName.trim()) { setGroupError('Group name is required'); return; }
          if (needsPrimary && !groupPrimaryId) { setGroupError('Select a primary guest'); return; }
          setGroupError('');
          setGroupSaving(true);
          try {
            const payload = {
              guest_ids: selectedIds,
              group_name: groupName.trim(),
              primary_guest_id: needsPrimary ? groupPrimaryId : '',
            };
            if (selectedExisting) {
              payload.existing_party_id = selectedExisting.id;
            }
            const res = await fetch(`${API}/api/guests/bulk-group`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            });
            if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Grouping failed'); }
            toast.success(`${selectedIds.length} guests grouped into "${groupName.trim()}"`);
            setShowGroupModal(false);
            setSelectedIds([]);
            refresh?.();
          } catch (err) { setGroupError(err.message || 'Grouping failed'); }
          setGroupSaving(false);
        };
        return (
          <div data-testid="group-modal-backdrop" style={{
            position: 'fixed', inset: 0, zIndex: 2000,
            background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }} onClick={(e) => { if (e.target === e.currentTarget) setShowGroupModal(false); }}>
            <div data-testid="group-modal" style={{
              width: 460, maxHeight: '80vh', overflow: 'auto',
              background: 'rgba(255,255,255,0.97)', border: '1px solid rgba(0,0,0,0.10)', borderRadius: 16,
              boxShadow: '0 24px 80px rgba(0,0,0,0.18)', padding: 0,
            }}>
              {/* Header */}
              <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#1A1A1A', fontFamily: "Georgia, serif" }}>Group Selected Guests</div>
                <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>{selectedGuests.length} guests selected</div>
              </div>
              {/* Body */}
              <div style={{ padding: '16px 24px' }}>
                {/* Group Name */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#1A1A1A', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 }}>
                    Group Name <span style={{ color: '#E53E3E' }}>*</span>
                  </label>
                  <input
                    data-testid="group-name-input"
                    value={groupName}
                    onChange={e => setGroupName(e.target.value)}
                    placeholder="e.g. The Smith Family"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, boxSizing: 'border-box',
                      border: '1px solid rgba(0,0,0,0.15)', background: '#fff',
                      fontSize: 14, color: '#1A1A1A', fontFamily: "Georgia, serif", outline: 'none' }}
                  />
                  {existingParties.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ fontSize: 10, color: '#888', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Or join existing group:</div>
                      <div style={{ maxHeight: 160, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4, border: '1px solid rgba(0,0,0,0.08)', borderRadius: 8, padding: 6 }}>
                        {existingParties.map(p => (
                          <button key={p.id} onClick={() => { setGroupName(p.name); if (p.has_primary) setGroupPrimaryId(''); }}
                            data-testid={`existing-group-${p.id}`}
                            style={{ padding: '8px 12px', borderRadius: 6, fontSize: 13, cursor: 'pointer', textAlign: 'left',
                              border: groupName === p.name ? '1.5px solid #1A1A1A' : '1px solid rgba(0,0,0,0.08)',
                              background: groupName === p.name ? 'rgba(0,0,0,0.04)' : '#fff',
                              color: '#1A1A1A', fontFamily: "Georgia, serif", fontWeight: groupName === p.name ? 600 : 400,
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span>{p.name}</span>
                            {p.has_primary && <span style={{ fontSize: 9, color: '#888', fontWeight: 500, fontFamily: "'DM Sans', sans-serif" }}>Has primary</span>}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {/* Primary Guest Selection — only if needed */}
                {needsPrimary && (
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#1A1A1A', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 }}>
                      Primary Guest <span style={{ color: '#E53E3E' }}>*</span>
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {selectedGuests.map(g => (
                        <label key={g.id} data-testid={`primary-option-${g.id}`}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
                            border: groupPrimaryId === g.id ? '1.5px solid #1A1A1A' : '1px solid rgba(0,0,0,0.10)',
                            background: groupPrimaryId === g.id ? 'rgba(0,0,0,0.03)' : '#fff',
                            transition: 'all 150ms' }}>
                          <input type="radio" name="primaryGuest" value={g.id} checked={groupPrimaryId === g.id}
                            onChange={() => setGroupPrimaryId(g.id)}
                            style={{ accentColor: '#1A1A1A' }} />
                          <span style={{ fontSize: 13, fontWeight: groupPrimaryId === g.id ? 700 : 400,
                            color: '#1A1A1A', fontFamily: "Georgia, serif" }}>
                            {g.first_name} {g.last_name}
                          </span>
                          {g.email && <span style={{ fontSize: 10, color: '#999', marginLeft: 'auto' }}>{g.email}</span>}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {!needsPrimary && (
                  <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 12,
                    background: 'rgba(46,212,122,0.08)', border: '1px solid rgba(46,212,122,0.25)',
                    color: '#2F7A57', fontSize: 12, fontFamily: "'DM Sans', sans-serif" }}>
                    This group already has a primary guest assigned. Selected guests will be added directly.
                  </div>
                )}
                {/* Error */}
                {groupError && (
                  <div data-testid="group-error" style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 12,
                    background: 'rgba(229,62,62,0.08)', border: '1px solid rgba(229,62,62,0.25)',
                    color: '#E53E3E', fontSize: 12 }}>
                    {groupError}
                  </div>
                )}
              </div>
              {/* Footer */}
              <div style={{ padding: '12px 24px 20px', display: 'flex', gap: 10, justifyContent: 'flex-end', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                <button onClick={() => setShowGroupModal(false)} style={{ padding: '10px 20px', borderRadius: 8,
                  border: '1px solid rgba(0,0,0,0.15)', background: '#fff', color: '#666',
                  fontSize: 13, cursor: 'pointer' }}>
                  Cancel
                </button>
                <button data-testid="group-save-btn" onClick={handleGroupSave} disabled={groupSaving}
                  style={{ padding: '10px 24px', borderRadius: 8,
                    border: '1px solid #1A1A1A', background: '#1A1A1A',
                    color: '#fff', fontSize: 13, fontWeight: 700, cursor: groupSaving ? 'not-allowed' : 'pointer',
                    opacity: groupSaving ? 0.6 : 1 }}>
                  {groupSaving ? 'Grouping...' : selectedExisting ? 'Add to Group' : 'Create Group'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ═══ FIELD-LEVEL MERGE MODAL (Feb 2026, Issue #1) ═══
          When user clicks Merge (2) in the bulk bar, we open this modal so
          they pick which value wins for each field — no blind merge that risks
          losing data. Survivor = guest A (first selected). Guest B is
          soft-deleted afterwards. Calls existing /api/guests/merge endpoint
          which already supports field_selections. */}
      {mergeModalOpen && (() => {
        const a = guests.find(g => g.id === selectedIds[0]);
        const b = guests.find(g => g.id === selectedIds[1]);
        if (!a || !b) return null;
        const partyName = (gid) => {
          const g = guests.find(x => x.id === gid);
          return g?.party_name || g?.group_name || (gid ? '(unnamed party)' : '—');
        };
        const renderVal = (field, g) => {
          const v = g[field];
          if (v === null || v === undefined || v === '') return <span style={{ color: '#999', fontStyle: 'italic' }}>(empty)</span>;
          if (field === 'party_id') return partyName(v);
          if (field === 'rsvp_status') return (v || '').toString().toUpperCase() || '—';
          if (field === 'age_group') return (v || '').toString().toUpperCase() || '—';
          return String(v);
        };
        const fieldLabels = {
          first_name: 'First Name',
          last_name: 'Last Name',
          email: 'Email',
          phone: 'Phone',
          age_group: 'Age Group',
          rsvp_status: 'RSVP Status',
          party_id: 'Party / Group',
          other_notes: 'Notes',
          allergies: 'Allergies',
          dietary_requirements: 'Dietary',
          source_detail: 'Source Detail',
        };
        const fields = Object.keys(fieldLabels);
        const handleMerge = async () => {
          setMergeSaving(true);
          try {
            const res = await fetch(`${API}/api/guests/merge`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                guest_id_a: a.id,
                guest_id_b: b.id,
                field_selections: mergeFieldSelections,
              }),
            });
            if (!res.ok) {
              const err = await res.json().catch(() => ({}));
              throw new Error(err.detail || 'Merge failed');
            }
            toast.success(`Merged ${b.first_name} ${b.last_name} into ${a.first_name} ${a.last_name}`);
            setMergeModalOpen(false);
            setMergeFieldSelections({});
            setSelectedIds([]);
            refresh?.();
            fetchPlusOneItems();
          } catch (e) {
            toast.error(e.message || 'Merge failed');
          }
          setMergeSaving(false);
        };
        return (
          <div data-testid="merge-modal-backdrop" style={{
            position: 'fixed', inset: 0, zIndex: 2000,
            background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }} onClick={(e) => { if (e.target === e.currentTarget && !mergeSaving) setMergeModalOpen(false); }}>
            <div data-testid="merge-modal" style={{
              width: 720, maxWidth: '94vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column',
              background: 'rgba(255,255,255,0.98)', border: '1px solid rgba(0,0,0,0.10)', borderRadius: 16,
              boxShadow: '0 24px 80px rgba(0,0,0,0.18)',
            }}>
              {/* Header */}
              <div style={{ padding: '18px 24px 14px', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#1A1A1A', fontFamily: "Georgia, serif" }}>Merge Guests — Choose Surviving Values</div>
                <div style={{ fontSize: 12, color: '#888', marginTop: 4, lineHeight: 1.5 }}>
                  Survivor: <strong style={{ color: '#1A1A1A' }}>{a.first_name} {a.last_name}</strong> (kept).
                  Merged-in: <strong style={{ color: '#B24E4E' }}>{b.first_name} {b.last_name}</strong> (will be soft-deleted).
                  Pick the value to keep for each field.
                </div>
              </div>
              {/* Body — table of A vs B */}
              <div style={{ flex: 1, overflow: 'auto', padding: '8px 0' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 1fr', gap: 0, fontSize: 12, fontFamily: "'DM Sans', sans-serif" }}>
                  {/* Header row */}
                  <div style={{ padding: '10px 16px', fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 10, borderBottom: '1px solid rgba(0,0,0,0.10)' }}>Field</div>
                  <div style={{ padding: '10px 16px', fontWeight: 700, color: '#1A1A1A', borderBottom: '1px solid rgba(0,0,0,0.10)', borderLeft: '1px dashed rgba(0,0,0,0.08)' }}>
                    A — Survivor
                    <div style={{ fontSize: 10, color: '#888', fontWeight: 500, marginTop: 2 }}>{a.first_name} {a.last_name}</div>
                  </div>
                  <div style={{ padding: '10px 16px', fontWeight: 700, color: '#B24E4E', borderBottom: '1px solid rgba(0,0,0,0.10)', borderLeft: '1px dashed rgba(0,0,0,0.08)' }}>
                    B — Merged-in
                    <div style={{ fontSize: 10, color: '#888', fontWeight: 500, marginTop: 2 }}>{b.first_name} {b.last_name}</div>
                  </div>
                  {fields.map(f => {
                    const sel = mergeFieldSelections[f] || 'a';
                    const av = a[f]; const bv = b[f];
                    const aEmpty = av === null || av === undefined || av === '';
                    const bEmpty = bv === null || bv === undefined || bv === '';
                    const sameVal = !aEmpty && !bEmpty && JSON.stringify(av) === JSON.stringify(bv);
                    const noConflict = sameVal || (aEmpty && bEmpty);
                    return (
                      <React.Fragment key={f}>
                        <div style={{ padding: '12px 16px', color: '#444', fontWeight: 600, borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center' }}>
                          {fieldLabels[f]}
                          {noConflict && <span style={{ marginLeft: 8, fontSize: 9, color: '#888', fontStyle: 'italic' }}>no conflict</span>}
                        </div>
                        <div
                          data-testid={`merge-field-${f}-a`}
                          onClick={() => setMergeFieldSelections(prev => ({ ...prev, [f]: 'a' }))}
                          style={{
                            padding: '12px 16px', cursor: 'pointer', borderLeft: '1px dashed rgba(0,0,0,0.08)', borderBottom: '1px solid rgba(0,0,0,0.06)',
                            background: sel === 'a' ? 'rgba(63,199,204,0.10)' : 'transparent',
                            borderTop: sel === 'a' ? '2px solid #3FC7CC' : '2px solid transparent',
                            color: '#1A1A1A',
                            transition: 'background 0.15s',
                          }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input type="radio" checked={sel === 'a'} readOnly style={{ accentColor: '#3FC7CC', cursor: 'pointer' }} />
                            <span style={{ fontSize: 13 }}>{renderVal(f, a)}</span>
                          </div>
                        </div>
                        <div
                          data-testid={`merge-field-${f}-b`}
                          onClick={() => setMergeFieldSelections(prev => ({ ...prev, [f]: 'b' }))}
                          style={{
                            padding: '12px 16px', cursor: 'pointer', borderLeft: '1px dashed rgba(0,0,0,0.08)', borderBottom: '1px solid rgba(0,0,0,0.06)',
                            background: sel === 'b' ? 'rgba(178,78,78,0.08)' : 'transparent',
                            borderTop: sel === 'b' ? '2px solid #B24E4E' : '2px solid transparent',
                            color: '#1A1A1A',
                            transition: 'background 0.15s',
                          }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input type="radio" checked={sel === 'b'} readOnly style={{ accentColor: '#B24E4E', cursor: 'pointer' }} />
                            <span style={{ fontSize: 13 }}>{renderVal(f, b)}</span>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
                <div style={{ padding: '12px 24px', fontSize: 11, color: '#888', fontStyle: 'italic', fontFamily: "'DM Sans', sans-serif" }}>
                  Boolean status flags (sent invitation, sent thank-you, etc.) are merged with OR — TRUE wins.
                  Default selections favor non-empty values.
                </div>
              </div>
              {/* Footer */}
              <div style={{ padding: '14px 24px', display: 'flex', gap: 10, justifyContent: 'flex-end', borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                <button data-testid="merge-cancel-btn" onClick={() => { if (!mergeSaving) { setMergeModalOpen(false); setMergeFieldSelections({}); } }} disabled={mergeSaving}
                  style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.15)', background: '#fff', color: '#666', fontSize: 13, cursor: mergeSaving ? 'not-allowed' : 'pointer', opacity: mergeSaving ? 0.6 : 1 }}>
                  Cancel
                </button>
                <button data-testid="merge-confirm-btn" onClick={handleMerge} disabled={mergeSaving}
                  style={{ padding: '10px 24px', borderRadius: 8, border: '1px solid #3FC7CC', background: '#3FC7CC', color: '#fff', fontSize: 13, fontWeight: 700, cursor: mergeSaving ? 'not-allowed' : 'pointer', opacity: mergeSaving ? 0.6 : 1 }}>
                  {mergeSaving ? 'Merging…' : `Merge — Keep ${a.first_name}`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default GuestListTab;
